import { MailboxConfiguration } from "./features/mailboxes/MailboxConfiguration";
import { CaseWorkspace } from "./features/cases/CaseWorkspace";
import { BasicConfiguration } from "./features/configuration/BasicConfiguration";
import {
  loadRecentCaseUnreadSummary,
  type RecentCaseUnreadSummary,
} from "./features/cases/api";
import {
  connectCaseUnreadRealtime,
  resetCaseRealtimeConnection,
} from "./features/cases/realtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Banner,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  ChipSize,
  ChipVariant,
  CrmIconButton,
  CrmSelect,
  IconAdjustments,
  IconAlertTriangle,
  IconArrowDown,
  IconArrowUp,
  IconBuilding,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconColumns,
  IconCommand,
  IconCode,
  IconDotsVertical,
  IconFilter,
  IconLayoutKanban,
  IconLayoutList,
  IconLayoutNavbar,
  IconMailPlus,
  IconRefresh,
  IconSearch,
  IconUsers,
  IconBriefcase,
  IconX,
  Loader,
  SearchInput,
  Status,
  Tag,
} from "./twenty-ui";
import { describeCoreError } from "./core-api/errors";
import {
  loadAuthenticatedIdentity,
  loadAuthenticatedWorkspaces,
  loadAccountPasswordAuthStatus,
  loadFutureceedSsoStatus,
  loginAccountPassword,
  loginLocalUatPassword,
  logoutAuthenticatedSession,
  selectAuthenticatedWorkspace,
  type AuthenticatedIdentity,
  type AuthenticatedWorkspace,
} from "./core-api/auth";
import { CoreApiError } from "./core-api/api-client";
import { SyncedMailList } from "./features/gmail/SyncedMailList";
import { InquiryCenterGmailSyncAction } from "./features/gmail/InquiryCenterGmailSyncAction";
import {
  canUseDevTesterTestMail,
  DevTesterTestMailWorkspace,
} from "./features/gmail/DevTesterTestMailAction";
import {
  canonicalizeInquiryCenterView,
  resolveLocalUatPostLoginUrl,
  resolveInquiryCenterExperience,
} from "./inquiry-center-route";
import {
  loadGmailMailboxes,
  GMAIL_READONLY_SCOPES,
  startGmailOAuth,
  revokeGmailAccess,
  syncGmailRecovery,
  type GmailMailbox,
} from "./core-api/gmail";
import {
  loadRoleNotificationSmtpStatus,
  saveRoleNotificationSmtpCredentials,
  type RoleNotificationSmtpStatus,
} from "./core-api/role-notification-smtp";
import {
  loadInquiry,
  loadInquiryWorkspace,
  probeInquirySourceSchema,
  searchInquiries,
  setPriority,
  setRisk,
} from "./core-api/inquiries";
import {
  loadCase,
  loadCases,
  loadContact,
  loadContacts,
  loadCustomer,
  loadCustomers,
  loadOperationsContext,
  loadOverview,
  searchOperations,
} from "./core-api/operations";
import type {
  InquiryDashboard,
  InquiryDetail,
  InquirySource,
  InquirySummary,
} from "./core-contracts/inquiries";
import type {
  CaseDetail,
  CaseSummary,
  ContactDetail,
  ContactSummary,
  CustomerDetail,
  CustomerSummary,
  OperationsContext,
  OperationsKind,
  OperationsSearchGroup,
  OverviewDashboard,
  OverviewPeriod,
} from "./core-contracts/operations";
import WorkforceDevApp, {
  type WorkforceView,
} from "./features/workforce/WorkforceDevApp";
import CommercialDevelopmentApp from "./features/commercial/CommercialDevelopmentApp";
import type { CommercialTab } from "./features/commercial/types";
import { ShadowFlowWorkspace } from "./features/shadow-flow/ShadowFlowWorkspace";
import { CoreBusinessFlowWorkspace } from "./features/core-business-flow/CoreBusinessFlowWorkspace";
import { TriageWorkspace } from "./features/triage/TriageWorkspace";
import { PeopleWorkAllocation } from "./features/triage/PeopleWorkAllocation";
import { RoleManagement } from "./features/roles/RoleManagement";
import { UserManagement } from "./features/users/UserManagement";
import { WorkspaceManagement } from "./features/workspaces/WorkspaceManagement";
import { TeamManagement } from "./features/teams/TeamManagement";
import { PersonalSettings } from "./features/personal-settings/PersonalSettings";
import { EmailIntelligenceReview } from "./features/email-intelligence/EmailIntelligenceReview";
import {
  businessTypeLabel,
  confidenceLabel,
  objectTypeLabel,
  statusLabel,
} from "./localization/zh-CN";

type WorkforceViewKey = WorkforceView;
type CommercialViewKey =
  | "suppliers"
  | "supplier-rfqs"
  | "supplier-quotes"
  | "customer-quotes"
  | "approvals";
type ViewKey =
  | "overview"
  | "personal-settings"
  | "inquiry-center"
  | "approvals-exceptions"
  | "inbox"
  | "risk"
  | "sources"
  | OperationsKind
  | WorkforceViewKey
  | CommercialViewKey
  | "shadow-flow"
  | "people-work-allocation"
  | "role-management"
  | "user-management"
  | "cases-won"
  | "cases-returned"
  | "cases-failed"
  | "configuration-basic"
  | "configuration-workspaces"
  | "configuration-mailboxes"
  | "email-intelligence"
  | "developer-test-mail";

const VIEW_LABELS: Record<ViewKey, string> = {
  overview: "总览",
  "personal-settings": "个人设置",
  "inquiry-center": "询单分发",
  "approvals-exceptions": "异常与复核",
  inbox: "询单收件箱",
  risk: "风险审核",
  sources: "数据源状态",
  customers: "组织管理",
  contacts: "客户管理",
  cases: "待处理",
  "cases-won": "已成单",
  "cases-returned": "已退货",
  "cases-failed": "已失败",
  "my-work": "我的工作",
  dispatch: "待分发",
  "team-queue": "团队队列",
  "team-members": "团队管理",
  "sla-escalation": "SLA 与升级",
  suppliers: "供应商管理",
  "supplier-rfqs": "供应商询价",
  "supplier-quotes": "供应商报价",
  "customer-quotes": "客户报价",
  approvals: "审批",
  "shadow-flow": "AI 影子流程",
  "people-work-allocation": "人员与分工",
  "role-management": "角色管理",
  "user-management": "用户管理",
  "configuration-basic": "基础配置",
  "configuration-workspaces": "空间管理",
  "configuration-mailboxes": "邮箱配置和授权",
  "email-intelligence": "邮件智能复核",
  "developer-test-mail": "发送测试邮件",
};

const WORKFORCE_TABS: ViewKey[] = [
  "my-work",
  "dispatch",
  "team-queue",
  "team-members",
  "sla-escalation",
];
const COMMERCIAL_TABS: ViewKey[] = [
  "suppliers",
  "supplier-rfqs",
  "supplier-quotes",
  "customer-quotes",
  "approvals",
];

function getParam(key: string) {
  return new URLSearchParams(window.location.search).get(key) ?? "";
}

function statusColor(status: string) {
  if (["CLEAR", "RESOLVED", "HEALTHY", "READY_FOR_TEST"].includes(status))
    return "green";
  if (["HIGH", "URGENT", "REVIEW_REQUIRED", "FAILED"].includes(status))
    return "red";
  if (["PAUSED", "MISSING"].includes(status)) return "orange";
  return "blue";
}

function ModeTag({ demo }: { demo: boolean }) {
  return (
    <Tag
      color={demo ? "purple" : "green"}
      text={demo ? "本地 UAT" : "正式"}
      variant="border"
    />
  );
}

function PlannedFeatureWorkspace({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <section className="planned-feature" aria-label={title}>
      <div className="eyebrow">功能规划</div>
      <h1>{title}</h1>
      <p>{description}</p>
      <span>侧边栏位置已经确定，业务功能将在对应模块开发时接入。</span>
    </section>
  );
}

type SidebarNavItem = readonly [
  ViewKey,
  string,
  typeof IconLayoutList,
  boolean,
  number?,
];

function SidebarLink({
  item: [key, label, Icon, allowed, unreadCount],
  view,
  onView,
  subitem = false,
}: {
  item: SidebarNavItem;
  view: ViewKey;
  onView: (view: ViewKey) => void;
  subitem?: boolean;
}) {
  if (!allowed) return null;
  return (
    <button
      type="button"
      aria-label={label}
      className={`${subitem ? "nav-item nav-subitem" : "nav-item"}${
        view === key ? " is-active" : ""
      }`}
      onClick={() => onView(key)}
    >
      <Icon size={subitem ? 15 : 16} />
      <span>{label}</span>
      {unreadCount ? (
        <span
          className="nav-unread-count"
          aria-label={`最近一个月 ${unreadCount} 条未读消息`}
          title={`最近一个月 ${unreadCount} 条未读消息`}
        >
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      ) : null}
    </button>
  );
}

function SidebarGroup({
  label,
  icon: Icon,
  items,
  view,
  open,
  onToggle,
  onView,
  unreadCount,
}: {
  label: string;
  icon: typeof IconLayoutList;
  items: readonly SidebarNavItem[];
  view: ViewKey;
  open: boolean;
  onToggle: () => void;
  onView: (view: ViewKey) => void;
  unreadCount?: number;
}) {
  const visibleItems = items.filter(([, , , allowed]) => allowed);
  if (!visibleItems.length) return null;
  return (
    <>
      <button
        type="button"
        aria-label={label}
        className={`nav-item${
          visibleItems.some(([key]) => key === view) ? " is-active" : ""
        }`}
        aria-expanded={open}
        onClick={onToggle}
      >
        <Icon size={16} />
        <span>{label}</span>
        {unreadCount ? (
          <span
            className="nav-unread-count"
            aria-label={`最近一个月 ${unreadCount} 条未读消息`}
            title={`最近一个月 ${unreadCount} 条未读消息`}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        ) : null}
        {open ? (
          <IconChevronDown size={14} aria-hidden="true" />
        ) : (
          <IconChevronRight size={14} aria-hidden="true" />
        )}
      </button>
      {open
        ? visibleItems.map((item) => (
            <SidebarLink
              key={item[0]}
              item={item}
              view={view}
              onView={onView}
              subitem
            />
          ))
        : null}
    </>
  );
}

function AppShell({
  view,
  onView,
  children,
  demo,
  onCommand,
  context,
  userName,
  administratorType,
  workspaces,
  currentWorkspaceId,
  onSwitchWorkspace,
  onLogout,
}: {
  view: ViewKey;
  onView: (view: ViewKey) => void;
  children: React.ReactNode;
  demo: boolean;
  onCommand: () => void;
  context?: OperationsContext;
  userName?: string;
  administratorType?: AuthenticatedIdentity["user"]["administratorType"];
  workspaces: AuthenticatedWorkspace[];
  currentWorkspaceId?: string;
  onSwitchWorkspace: (workspaceId: string) => void;
  onLogout: () => void;
}) {
  const [navOpen, setNavOpen] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(
    ["inquiry-center", "approvals-exceptions", "email-intelligence"].includes(
      view,
    ),
  );
  const [caseOpen, setCaseOpen] = useState(
    ["cases", "cases-won", "cases-returned", "cases-failed"].includes(view),
  );
  const [customerOpen, setCustomerOpen] = useState(
    ["contacts", "customers"].includes(view),
  );
  const [accessOpen, setAccessOpen] = useState(
    ["user-management", "role-management", "team-members"].includes(view),
  );
  const [configurationOpen, setConfigurationOpen] = useState(
    [
      "configuration-basic",
      "configuration-workspaces",
      "configuration-mailboxes",
    ].includes(view),
  );
  const [developerOpen, setDeveloperOpen] = useState(
    view === "developer-test-mail",
  );
  const [caseUnreadSummary, setCaseUnreadSummary] =
    useState<RecentCaseUnreadSummary>();
  useEffect(() => {
    if (
      ["inquiry-center", "approvals-exceptions", "email-intelligence"].includes(
        view,
      )
    )
      setInquiryOpen(true);
    if (["cases", "cases-won", "cases-returned", "cases-failed"].includes(view))
      setCaseOpen(true);
    if (["contacts", "customers"].includes(view)) setCustomerOpen(true);
    if (["user-management", "role-management", "team-members"].includes(view))
      setAccessOpen(true);
    if (
      [
        "configuration-basic",
        "configuration-workspaces",
        "configuration-mailboxes",
      ].includes(view)
    )
      setConfigurationOpen(true);
    if (view === "developer-test-mail") setDeveloperOpen(true);
  }, [view]);
  const permissions = context?.permissions;
  const triageOnly = permissions?.triageOnly ?? false;
  const developerTestingEnabled = canUseDevTesterTestMail(
    context?.code,
    window.location.hostname,
  );
  const overviewItem: SidebarNavItem = [
    "overview",
    "总览",
    IconLayoutList,
    true,
  ];
  const personalSettingsItem: SidebarNavItem = [
    "personal-settings",
    "个人设置",
    IconUsers,
    Boolean(context),
  ];
  const inquiryItems: SidebarNavItem[] = [
    [
      "inquiry-center",
      "询单分发",
      IconLayoutList,
      permissions?.inquiryRead ?? false,
    ],
    [
      "approvals-exceptions",
      "风险待审核",
      IconAlertTriangle,
      triageOnly ||
        (permissions?.approvalRead ?? false) ||
        (permissions?.inquiryReview ?? false),
    ],
    [
      "email-intelligence",
      "邮件智能复核",
      IconBriefcase,
      permissions?.emailIntelligenceRead ?? false,
    ],
  ];
  const caseItems: SidebarNavItem[] = [
    [
      "cases",
      "待处理",
      IconBriefcase,
      canViewCaseQueue("cases", permissions),
      caseUnreadSummary?.byOutcome.PENDING,
    ],
    [
      "cases-won",
      "已成单",
      IconBriefcase,
      canViewCaseQueue("cases-won", permissions),
      caseUnreadSummary?.byOutcome.WON,
    ],
    [
      "cases-returned",
      "已退货",
      IconBriefcase,
      canViewCaseQueue("cases-returned", permissions),
      caseUnreadSummary?.byOutcome.RETURNED,
    ],
    [
      "cases-failed",
      "已失败",
      IconBriefcase,
      canViewCaseQueue("cases-failed", permissions),
      caseUnreadSummary?.byOutcome.FAILED,
    ],
  ];
  useEffect(() => {
    setCaseUnreadSummary(undefined);
    if (!context?.id || !permissions?.caseRead) return;
    let controller: AbortController | undefined;
    let refreshTimer: number | undefined;
    const refresh = () => {
      controller?.abort();
      controller = new AbortController();
      void loadRecentCaseUnreadSummary(controller.signal)
        .then(setCaseUnreadSummary)
        .catch((error: unknown) => {
          if (!(error instanceof DOMException && error.name === "AbortError")) {
            // The case pages remain usable when the optional navigation summary is unavailable.
          }
        });
    };
    const scheduleRefresh = () => {
      if (refreshTimer) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(refresh, 400);
    };
    refresh();
    const disconnect = connectCaseUnreadRealtime({
      onUnreadChanged: scheduleRefresh,
      onReconnect: scheduleRefresh,
    });
    const onVisibility = () => {
      if (document.visibilityState === "visible") scheduleRefresh();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      controller?.abort();
      if (refreshTimer) window.clearTimeout(refreshTimer);
      disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [context?.id, permissions?.caseRead]);
  const supplierItem: SidebarNavItem = [
    "suppliers",
    "供应商管理",
    IconBuilding,
    !triageOnly && (permissions?.supplierRead ?? false),
  ];
  const customerItems: SidebarNavItem[] = [
    ["contacts", "客户管理", IconUsers, permissions?.customerRead ?? false],
    ["customers", "组织管理", IconBuilding, permissions?.customerRead ?? false],
  ];
  const accessItems: SidebarNavItem[] = [
    ["user-management", "用户管理", IconUsers, permissions?.userRead ?? false],
    ["role-management", "角色管理", IconUsers, permissions?.roleRead ?? false],
    [
      "team-members",
      "团队管理",
      IconUsers,
      permissions?.workforceTeamRead ?? false,
    ],
  ];
  const configurationItems: SidebarNavItem[] = [
    [
      "configuration-workspaces",
      "空间管理",
      IconBuilding,
      Boolean(permissions?.workspaceManage || permissions?.workspaceCreate),
    ],
    [
      "configuration-basic",
      "基础配置",
      IconAdjustments,
      Boolean(
        permissions?.caseMetricDefinitionsManage ||
        (permissions?.userManage && permissions?.roleManage),
      ),
    ],
    [
      "configuration-mailboxes",
      "邮箱配置和授权",
      IconMailPlus,
      permissions?.mailboxRead ?? false,
    ],
  ];
  const developerItems: SidebarNavItem[] = [
    [
      "developer-test-mail",
      "发送测试邮件",
      IconMailPlus,
      Boolean(
        administratorType === "SUPER_ADMIN" ||
        developerTestingEnabled ||
        (permissions?.userManage && permissions?.roleManage),
      ),
    ],
  ];
  const workspaceName = context?.name ?? "工作空间加载失败";
  const profileName = context
    ? context.profile === "EMBODIED_INTELLIGENCE"
      ? "具身智能业务"
      : "化学试剂业务"
    : "请刷新页面重试";
  return (
    <div className="app-shell">
      <button
        className="mobile-menu"
        type="button"
        aria-label="打开导航"
        onClick={() => setNavOpen(true)}
      >
        <IconLayoutNavbar size={18} />
      </button>
      <aside
        className={navOpen ? "app-sidebar is-open" : "app-sidebar"}
        aria-label="CRM 主导航"
      >
        <div className="app-logo">
          <span className="app-logo-mark">C</span>
          <span>
            <strong>CRM 运营中心</strong>
            <small>核心工作空间</small>
          </span>
        </div>
        <div className="workspace-context">
          <span className="context-label">工作空间</span>
          {workspaces.length ? (
            <select
              aria-label="切换工作空间"
              value={currentWorkspaceId ?? ""}
              onChange={(event) => onSwitchWorkspace(event.target.value)}
            >
              {!currentWorkspaceId ? (
                <option value="">请选择工作空间</option>
              ) : null}
              {workspaces.map((workspace) => (
                <option key={workspace.id} value={workspace.id}>
                  {workspace.name}
                </option>
              ))}
            </select>
          ) : (
            <strong>{workspaceName}</strong>
          )}
          <span>{profileName}</span>
          {administratorType === "SUPER_ADMIN" ? (
            <span className="super-admin-badge">超级管理员 · 全部权限</span>
          ) : null}
          {context ? <ModeTag demo={demo} /> : null}
        </div>
        <nav className="app-nav">
          <SidebarLink
            item={overviewItem}
            view={view}
            onView={(next) => {
              onView(next);
              setNavOpen(false);
            }}
          />
          <SidebarLink
            item={personalSettingsItem}
            view={view}
            onView={(next) => {
              onView(next);
              setNavOpen(false);
            }}
          />
          <SidebarGroup
            label="询单分发"
            icon={IconLayoutList}
            items={inquiryItems}
            view={view}
            open={inquiryOpen}
            onToggle={() => setInquiryOpen((current) => !current)}
            onView={(next) => {
              onView(next);
              setNavOpen(false);
            }}
          />
          <SidebarGroup
            label="案件管理"
            icon={IconBriefcase}
            items={caseItems}
            view={view}
            open={caseOpen}
            unreadCount={caseUnreadSummary?.count}
            onToggle={() => setCaseOpen((current) => !current)}
            onView={(next) => {
              onView(next);
              setNavOpen(false);
            }}
          />
          <SidebarLink
            item={supplierItem}
            view={view}
            onView={(next) => {
              onView(next);
              setNavOpen(false);
            }}
          />
          <SidebarGroup
            label="客户"
            icon={IconBuilding}
            items={customerItems}
            view={view}
            open={customerOpen}
            onToggle={() => setCustomerOpen((current) => !current)}
            onView={(next) => {
              onView(next);
              setNavOpen(false);
            }}
          />
          <SidebarGroup
            label="用户与权限"
            icon={IconUsers}
            items={accessItems}
            view={view}
            open={accessOpen}
            onToggle={() => setAccessOpen((current) => !current)}
            onView={(next) => {
              onView(next);
              setNavOpen(false);
            }}
          />
          <SidebarGroup
            label="配置"
            icon={IconAdjustments}
            items={configurationItems}
            view={view}
            open={configurationOpen}
            onToggle={() => setConfigurationOpen((current) => !current)}
            onView={(next) => {
              onView(next);
              setNavOpen(false);
            }}
          />
          <SidebarGroup
            label="开发者工具"
            icon={IconCode}
            items={developerItems}
            view={view}
            open={developerOpen}
            onToggle={() => setDeveloperOpen((current) => !current)}
            onView={(next) => {
              onView(next);
              setNavOpen(false);
            }}
          />
        </nav>
        <div className="sidebar-foot">
          <span className="online-dot" /> CRM 核心服务已连接
        </div>
      </aside>
      {navOpen ? (
        <button
          className="nav-scrim"
          type="button"
          aria-label="关闭导航"
          onClick={() => setNavOpen(false)}
        />
      ) : null}
      <main className="app-main">
        <header className="topbar">
          <div className="breadcrumb">
            <span>{workspaceName}</span>
            <span>/</span>
            <strong>{VIEW_LABELS[view]}</strong>
          </div>
          <div className="topbar-actions">
            <BuildVersion />
            <Button
              variant="tertiary"
              size="small"
              Icon={IconCommand}
              ariaLabel="打开命令菜单"
              onClick={onCommand}
              title="命令菜单"
              type="button"
            />
            <Avatar placeholder="U" size="sm" />
            <span className="user-name">{userName ?? "用户信息加载失败"}</span>
            <button className="uat-logout" type="button" onClick={onLogout}>
              退出
            </button>
          </div>
        </header>
        <div className="page-scroll">
          <div className="page-frame">{children}</div>
        </div>
      </main>
    </div>
  );
}

function BuildVersion() {
  const coreRef = import.meta.env.VITE_CORE_BUILD_REF ?? "local";
  const coreTime = import.meta.env.VITE_CORE_BUILD_TIME ?? "";
  const frontendRef = import.meta.env.VITE_FRONTEND_BUILD_REF ?? "local";
  const frontendTime = import.meta.env.VITE_FRONTEND_BUILD_TIME ?? "";
  return (
    <span
      className="build-version"
      title={`Core ${coreTime} · Frontend ${frontendTime}`}
    >
      Core {coreRef} · Frontend {frontendRef}
    </span>
  );
}

function PasswordLoginCard() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      try {
        await loginAccountPassword(username, password);
      } catch {
        await loginLocalUatPassword(username, password);
      }
      setPassword("");
      window.location.assign(
        resolveLocalUatPostLoginUrl({
          pathname: window.location.pathname,
          search: window.location.search,
          hash: window.location.hash,
        }),
      );
    } catch {
      setPassword("");
      setError("用户名或密码验证失败，请检查后重试。");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <section className="uat-login-card" aria-labelledby="uat-login-title">
      <div className="app-logo login-logo">
        <span className="app-logo-mark">C</span>
        <span>
          <strong>CRM 运营中心</strong>
          <small>账号登录</small>
        </span>
      </div>
      <div>
        <div className="eyebrow">CRM 账号</div>
        <h1 id="uat-login-title">登录 CRM</h1>
        <p>支持正式账号名；本地 UAT 账号仍保持兼容。</p>
      </div>
      <form className="uat-login-form" onSubmit={(event) => void submit(event)}>
        <label>
          用户名
          <input
            type="text"
            autoComplete="username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            required
            maxLength={320}
          />
        </label>
        <label>
          密码
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            minLength={6}
            maxLength={512}
          />
        </label>
        {error ? (
          <div className="uat-login-error" role="alert">
            {error}
          </div>
        ) : null}
        <Button
          type="submit"
          variant="primary"
          size="medium"
          title={submitting ? "正在验证…" : "登录"}
          disabled={submitting}
        />
      </form>
      <small className="uat-login-safety">
        不会使用真实邮件、客户或供应商外发通道。
      </small>
    </section>
  );
}

function LocalUatLogin() {
  return (
    <main className="uat-login-shell">
      <PasswordLoginCard />
    </main>
  );
}

function FutureceedLoginCard() {
  return (
    <section
      className="uat-login-card futureceed-login-card"
      aria-labelledby="futureceed-login-title"
    >
      <div className="app-logo login-logo">
        <span className="app-logo-mark">C</span>
        <span>
          <strong>CRM 运营中心</strong>
          <small>Futureceed</small>
        </span>
      </div>
      <div
        className="futureceed-handoff"
        aria-label="Futureceed 安全登录到 CRM"
      >
        <span>futureceed.com</span>
        <i aria-hidden="true" />
        <span>CRM</span>
      </div>
      <div>
        <div className="eyebrow">统一账号</div>
        <h1 id="futureceed-login-title">使用 Futureceed 登录</h1>
        <p>
          继续使用你现在的 Futureceed 用户名和密码。CRM 不会保存或复制你的密码。
        </p>
      </div>
      <a
        className="futureceed-login-action"
        href="/api/v1/auth/futureceed/login"
      >
        继续登录
        <span aria-hidden="true">→</span>
      </a>
      <small className="uat-login-safety">
        登录后，CRM 仍只显示当前账号有权限访问的工作空间。
      </small>
    </section>
  );
}

function AuthenticationRequired() {
  const [ssoEnabled, setSsoEnabled] = useState<boolean>();
  const [passwordEnabled, setPasswordEnabled] = useState<boolean>();
  const [statusFailed, setStatusFailed] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    void Promise.allSettled([
      loadFutureceedSsoStatus(controller.signal),
      loadAccountPasswordAuthStatus(controller.signal),
    ]).then(([sso, password]) => {
      if (sso.status === "rejected") {
        setStatusFailed(true);
        return;
      }
      setSsoEnabled(sso.value.enabled);
      setPasswordEnabled(
        password.status === "fulfilled" && password.value.enabled,
      );
    });
    return () => controller.abort();
  }, []);
  if (statusFailed) {
    return (
      <main className="uat-login-shell">
        <section className="uat-login-card" role="alert">
          <div className="eyebrow">登录服务</div>
          <h1>暂时无法连接</h1>
          <p>请刷新页面重试。如果仍然无法登录，请联系管理员检查 CRM 服务。</p>
        </section>
      </main>
    );
  }
  if (ssoEnabled === undefined || passwordEnabled === undefined)
    return <AppBootstrapSkeleton />;
  if (!ssoEnabled) return <LocalUatLogin />;
  return (
    <main className="uat-login-shell futureceed-login-shell">
      <div className="login-stack">
        <FutureceedLoginCard />
        {passwordEnabled ? <PasswordLoginCard /> : null}
      </div>
    </main>
  );
}

function AppBootstrapSkeleton() {
  return (
    <div className="app-bootstrap-shell" aria-label="正在加载 CRM 工作空间">
      <aside className="app-bootstrap-sidebar" aria-hidden="true">
        <div className="app-bootstrap-logo" />
        <div className="app-bootstrap-context" />
        <div className="app-bootstrap-nav">
          <span />
          <span />
        </div>
      </aside>
      <main className="app-bootstrap-main">
        <header className="app-bootstrap-topbar">
          <span />
          <span />
        </header>
        <div className="app-bootstrap-content">
          <div className="app-bootstrap-heading" />
          <div className="app-bootstrap-toolbar" />
          <div className="app-bootstrap-list">
            {Array.from({ length: 6 }, (_, index) => (
              <div key={index} />
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

function PageHeader({
  dashboard,
  view,
  onRefresh,
  context,
}: {
  dashboard?: InquiryDashboard;
  view: ViewKey;
  onRefresh: () => void;
  context?: OperationsContext;
}) {
  const title = context?.permissions.triageOnly
    ? view === "approvals-exceptions"
      ? "异常与复核"
      : view === "email-intelligence"
        ? "邮件智能复核"
        : "询单中心"
    : view === "overview"
      ? "总览"
      : view === "inbox" || view === "risk" || view === "sources"
        ? "询单工作台"
        : VIEW_LABELS[view];
  return (
    <div className="page-heading">
      <div>
        <div className="eyebrow">CRM 核心服务 · 运营中心</div>
        <div className="title-line">
          <h1>{title}</h1>
          {dashboard &&
          (view === "inbox" || view === "risk" || view === "sources") ? (
            <ModeTag demo={dashboard.mode === "DEMO"} />
          ) : null}
        </div>
      </div>
      <div className="heading-actions">
        <Button
          variant="secondary"
          size="small"
          Icon={IconRefresh}
          onClick={onRefresh}
          title="刷新"
          type="button"
        />
      </div>
    </div>
  );
}

function CompactTable({
  items,
  selectedId,
  onSelect,
}: {
  items: InquirySummary[];
  selectedId?: string;
  onSelect: (item: InquirySummary) => void;
}) {
  return (
    <div className="record-table" role="table">
      <div className="table-head" role="row">
        <span className="check-cell">
          <input type="checkbox" aria-label="选择全部" />
        </span>
        <span>询单 / 联系人</span>
        <span>产品 / 请求</span>
        <span>来源</span>
        <span>状态</span>
        <span>负责人</span>
        <span>时间</span>
      </div>
      {items.map((item) => (
        <button
          type="button"
          role="row"
          key={item.id}
          className={
            selectedId === item.id ? "table-row is-selected" : "table-row"
          }
          onClick={() => onSelect(item)}
        >
          <span className="check-cell">
            <input
              type="checkbox"
              tabIndex={-1}
              aria-label={`选择 ${item.sourceInquiryId}`}
              onClick={(event) => event.stopPropagation()}
            />
          </span>
          <span className="primary-cell">
            <strong>{item.contactName ?? item.normalizedEmail}</strong>
            <small>
              {item.companyName ?? "未确认组织"} · {item.countryDeclared ?? "—"}
            </small>
          </span>
          <span className="primary-cell">
            <strong>
              {item.productDisplayName ?? item.catalogNumber ?? "产品待确认"}
            </strong>
            <small>
              {item.catalogNumber ?? "无编号"} ·{" "}
              {item.quantityRaw ?? "数量待确认"}
            </small>
          </span>
          <span>
            <Chip
              label={item.sourceSystem}
              variant={Chip.Variant.Static}
              size={Chip.Size.Small}
            />
          </span>
          <span className="status-stack">
            <Status
              color={statusColor(item.riskDisposition) as never}
              text={statusLabel(item.riskDisposition)}
            />
            <small>{statusLabel(item.status)}</small>
          </span>
          <span className="owner-cell">
            <Avatar
              placeholder={
                item.assignedUserId ? item.assignedUserId.slice(0, 1) : "—"
              }
              size="sm"
            />
            <small>{item.assignedUserId ? "已分配" : "未分配"}</small>
          </span>
          <span className="time-cell">
            <strong>
              {new Date(item.sourceSubmittedAt).toLocaleDateString("zh-CN")}
            </strong>
            <small>
              {new Date(item.sourceSubmittedAt).toLocaleTimeString("zh-CN", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </small>
          </span>
        </button>
      ))}
    </div>
  );
}

function Toolbar({
  query,
  setQuery,
  site,
  setSite,
  status,
  setStatus,
  priority,
  setPriority,
  onClear,
}: {
  query: string;
  setQuery: (value: string) => void;
  site: string;
  setSite: (value: string) => void;
  status: string;
  setStatus: (value: string) => void;
  priority: string;
  setPriority: (value: string) => void;
  onClear: () => void;
}) {
  const hasFilters = Boolean(
    query || site !== "ALL" || status !== "ALL" || priority !== "ALL",
  );
  return (
    <>
      <div className="toolbar">
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder="搜索邮箱、产品、CAS 或询单编号"
          aria-label="搜索询单"
        />
        <CrmSelect
          label="来源"
          value={site}
          onChange={setSite}
          options={[
            { value: "ALL", label: "全部来源" },
            { value: "BENCHCHEM", label: "BenchChem" },
            { value: "SMOLECULE", label: "SMolecule" },
          ]}
        />
        <CrmSelect
          label="状态"
          value={status}
          onChange={setStatus}
          options={[
            { value: "ALL", label: "全部状态" },
            { value: "NEW", label: "新询单" },
            { value: "REVIEW_REQUIRED", label: "待审核" },
            { value: "CLOSED", label: "已关闭" },
          ]}
        />
        <CrmSelect
          label="优先级"
          value={priority}
          onChange={setPriority}
          options={[
            { value: "ALL", label: "优先级" },
            { value: "HIGH", label: "高" },
            { value: "URGENT", label: "紧急" },
          ]}
        />
        <Button
          variant="tertiary"
          size="small"
          Icon={IconFilter}
          title="更多筛选"
          type="button"
        />
        <Button
          variant="tertiary"
          size="small"
          Icon={IconColumns}
          title="列设置"
          type="button"
        />
        <Button
          variant="tertiary"
          size="small"
          Icon={IconArrowDown}
          title="最新优先"
          type="button"
        />
      </div>
      {hasFilters ? (
        <div className="active-filters">
          <span>当前筛选</span>
          {query ? (
            <Chip
              label={`搜索：${query}`}
              variant={Chip.Variant.Highlighted}
              size={Chip.Size.Small}
            />
          ) : null}
          {site !== "ALL" ? (
            <Chip
              label={site}
              variant={Chip.Variant.Highlighted}
              size={Chip.Size.Small}
            />
          ) : null}
          {status !== "ALL" ? (
            <Chip
              label={statusLabel(status)}
              variant={Chip.Variant.Highlighted}
              size={Chip.Size.Small}
            />
          ) : null}
          {priority !== "ALL" ? (
            <Chip
              label={statusLabel(priority)}
              variant={Chip.Variant.Highlighted}
              size={Chip.Size.Small}
            />
          ) : null}
          <button type="button" onClick={onClear}>
            清除全部
          </button>
        </div>
      ) : null}
    </>
  );
}

function Inbox({
  items,
  selectedId,
  onSelect,
  onBulk,
  busy,
}: {
  items: InquirySummary[];
  selectedId?: string;
  onSelect: (item: InquirySummary) => void;
  onBulk: () => void;
  busy: boolean;
}) {
  const [query, setQuery] = useState(getParam("q"));
  const [site, setSite] = useState(getParam("site") || "ALL");
  const [status, setStatus] = useState(getParam("status") || "ALL");
  const [priority, setPriority] = useState("ALL");
  const [savedView, setSavedView] = useState("all");
  const [sortAsc, setSortAsc] = useState(false);
  const [searching, setSearching] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => {
    window.clearTimeout(timer.current);
    if (query.trim().length < 3) return;
    timer.current = window.setTimeout(() => {
      setSearching(true);
      void searchInquiries(query).finally(() => setSearching(false));
    }, 240);
    return () => window.clearTimeout(timer.current);
  }, [query]);
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return items
      .filter((item) => {
        const searchable = [
          item.sourceInquiryId,
          item.normalizedEmail,
          item.contactName,
          item.companyName,
          item.catalogNumber,
          item.productDisplayName,
          item.casRaw,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return (
          (!needle || searchable.includes(needle)) &&
          (site === "ALL" || item.sourceSystem === site) &&
          (status === "ALL" || item.status === status) &&
          (priority === "ALL" || item.priority === priority) &&
          (savedView === "all" ||
            (savedView === "risk" && item.riskDisposition !== "CLEAR") ||
            (savedView === "unassigned" && !item.assignedUserId))
        );
      })
      .sort((a, b) =>
        sortAsc
          ? Date.parse(a.sourceSubmittedAt) - Date.parse(b.sourceSubmittedAt)
          : Date.parse(b.sourceSubmittedAt) - Date.parse(a.sourceSubmittedAt),
      );
  }, [items, priority, query, savedView, site, sortAsc, status]);
  const clear = () => {
    setQuery("");
    setSite("ALL");
    setStatus("ALL");
    setPriority("ALL");
  };
  return (
    <section className="inbox-layout">
      <aside className="view-rail">
        <div className="rail-heading">
          <span>视图</span>
          <Button
            variant="tertiary"
            size="small"
            title="新建视图"
            type="button"
          >
            +
          </Button>
        </div>
        {[
          ["all", "全部询单"],
          ["risk", "风险审核"],
          ["unassigned", "未分配"],
        ].map(([key, label]) => (
          <button
            type="button"
            key={key}
            className={savedView === key ? "rail-view is-active" : "rail-view"}
            onClick={() => setSavedView(key)}
          >
            <span>{label}</span>
            {key === "all" ? <strong>{items.length}</strong> : null}
          </button>
        ))}
        <div className="rail-divider" />
        <span className="rail-note">已保存视图 · 本地会话草稿</span>
      </aside>
      <div className="inbox-content">
        <div className="content-header">
          <div>
            <div className="eyebrow">询单收件箱</div>
            <h2>询单收件箱</h2>
          </div>
          <div className="content-count">{filtered.length} 条记录</div>
        </div>
        <Toolbar
          query={query}
          setQuery={setQuery}
          site={site}
          setSite={setSite}
          status={status}
          setStatus={setStatus}
          priority={priority}
          setPriority={setPriority}
          onClear={clear}
        />
        <div className="table-actions">
          <span>{searching ? "搜索中…" : "↑↓ 移动 · Enter 打开 · / 搜索"}</span>
          <Button
            variant="secondary"
            size="small"
            onClick={onBulk}
            disabled={busy || filtered.length === 0}
            type="button"
          >
            批量处理
          </Button>
          <Button
            variant="tertiary"
            size="small"
            Icon={sortAsc ? IconArrowUp : IconArrowDown}
            onClick={() => setSortAsc((value) => !value)}
            title="切换排序"
            type="button"
          >
            {sortAsc ? "最早优先" : "最新优先"}
          </Button>
        </div>
        {filtered.length ? (
          <CompactTable
            items={filtered}
            selectedId={selectedId}
            onSelect={onSelect}
          />
        ) : (
          <EmptyState
            title="当前视图没有记录"
            description="清除筛选或切换已保存视图。"
          />
        )}
      </div>
    </section>
  );
}

function EmptyState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="empty-state">
      <div className="empty-icon">—</div>
      <strong>{title}</strong>
      <span>{description}</span>
    </div>
  );
}

function Dashboard({
  overview,
  period,
  busy,
  onPeriod,
  onView,
}: {
  overview?: OverviewDashboard;
  period: OverviewPeriod;
  busy: boolean;
  onPeriod: (period: OverviewPeriod) => void;
  onView: (view: ViewKey) => void;
}) {
  const periods: Array<[OverviewPeriod, string]> = [
    ["TODAY", "今日"],
    ["LAST_7_DAYS", "近 7 天"],
    ["LAST_30_DAYS", "近 30 天"],
  ];
  return (
    <div className="overview-dashboard">
      <div className="overview-period" role="group" aria-label="总览时间范围">
        {periods.map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={period === value ? "active" : ""}
            aria-pressed={period === value}
            disabled={busy}
            onClick={() => onPeriod(value)}
          >
            {label}
          </button>
        ))}
      </div>
      {overview?.inquiryDistribution ? (
        <section
          className="overview-section"
          aria-labelledby="overview-inquiry-title"
        >
          <header>
            <div>
              <h2 id="overview-inquiry-title">询单分发</h2>
              <p>当前用户的询单分发统计</p>
            </div>
            <Button
              variant="secondary"
              size="small"
              onClick={() => onView("inquiry-center")}
            >
              进入询单分发 <IconChevronRight size={14} />
            </Button>
          </header>
          <div className="overview-metrics overview-metrics-three">
            <OverviewMetric
              label="总分发询单数"
              value={overview.inquiryDistribution.totalDistributed}
            />
            <OverviewMetric
              label="待分发数"
              value={overview.inquiryDistribution.pending}
            />
            <OverviewMetric
              label="风险待核实"
              value={overview.inquiryDistribution.riskReview}
            />
          </div>
        </section>
      ) : null}
      {overview?.caseManagement ? (
        <section
          className="overview-section"
          aria-labelledby="overview-case-title"
        >
          <header>
            <div>
              <h2 id="overview-case-title">案件管理</h2>
              <p>当前用户的案件数据</p>
            </div>
          </header>
          <div className="overview-metrics overview-metrics-four">
            <OverviewMetric
              label="进行中的（待处理）"
              value={overview.caseManagement.pending}
            />
            <OverviewMetric
              label="成单的"
              value={overview.caseManagement.won}
            />
            <OverviewMetric
              label="失败的"
              value={overview.caseManagement.failed}
            />
            <OverviewMetric
              label="退货的"
              value={overview.caseManagement.returned}
            />
          </div>
        </section>
      ) : null}
    </div>
  );
}

function OverviewMetric({ label, value }: { label: string; value: number }) {
  return (
    <div className="overview-metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function SourceHealth({
  sources,
  busy,
  onSchemaProbe,
}: {
  sources: InquirySource[];
  busy: boolean;
  onSchemaProbe: (sourceId: string) => void;
}) {
  const [gmailMailboxes, setGmailMailboxes] = useState<GmailMailbox[]>([]);
  const [gmailLoading, setGmailLoading] = useState(true);
  const [gmailFailure, setGmailFailure] = useState("");
  const [gmailStartingId, setGmailStartingId] = useState("");
  const [gmailRecoveringId, setGmailRecoveringId] = useState("");
  const [gmailNotice, setGmailNotice] = useState("");
  const [gmailAuthorizationUrls, setGmailAuthorizationUrls] = useState<
    Record<string, string>
  >({});
  const [smtpStatus, setSmtpStatus] =
    useState<RoleNotificationSmtpStatus | null>(null);
  const [smtpStatusLoading, setSmtpStatusLoading] = useState(true);
  const [smtpStatusFailure, setSmtpStatusFailure] = useState("");
  const [smtpVisible, setSmtpVisible] = useState(false);
  const [smtpUsername, setSmtpUsername] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpBusy, setSmtpBusy] = useState(false);
  const [smtpNotice, setSmtpNotice] = useState("");

  useEffect(() => {
    let active = true;
    void loadGmailMailboxes()
      .then((mailboxes) => {
        if (active) setGmailMailboxes(mailboxes);
      })
      .catch((error: unknown) => {
        if (active)
          setGmailFailure(
            error instanceof CoreApiError && error.status === 403
              ? "当前账号没有查看 Gmail 邮箱配置的权限。"
              : "Gmail 邮箱配置暂时无法加载。",
          );
      })
      .finally(() => {
        if (active) setGmailLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    void loadRoleNotificationSmtpStatus()
      .then((status) => {
        if (active) setSmtpStatus(status);
      })
      .catch((error: unknown) => {
        if (!active) return;
        setSmtpStatusFailure(
          error instanceof CoreApiError && error.status === 403
            ? "当前账号没有管理内部邮件分发凭据的权限。"
            : "内部邮件分发策略暂时无法加载。",
        );
      })
      .finally(() => {
        if (active) setSmtpStatusLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const saveSmtp = async () => {
    if (!smtpUsername.trim() || !smtpPassword) return;
    setSmtpBusy(true);
    setSmtpNotice("");
    try {
      const saved = await saveRoleNotificationSmtpCredentials(
        smtpUsername.trim(),
        smtpPassword,
      );
      setSmtpStatus(saved);
      setSmtpUsername("");
      setSmtpPassword("");
      setSmtpVisible(false);
      setSmtpNotice("SMTP 凭据已加密保存。录入凭据不会发送邮件。 ");
    } catch (error: unknown) {
      setSmtpNotice(
        error instanceof CoreApiError && error.status === 403
          ? "需要管理员密钥权限和近期 HIGH 级登录验证后才能保存。"
          : error instanceof CoreApiError && error.status === 409
            ? "服务端分发策略未就绪，请先检查 Gmail 邮箱和销售/查货团队收件策略。"
            : "SMTP 凭据未保存，请检查登录状态后重试。",
      );
    } finally {
      setSmtpBusy(false);
    }
  };

  const startGmail = async (mailbox: GmailMailbox) => {
    setGmailStartingId(mailbox.id);
    setGmailFailure("");
    try {
      const started = await startGmailOAuth(
        mailbox.id,
        mailbox.gmailAuthorization?.scopes ?? GMAIL_READONLY_SCOPES,
      );
      setGmailAuthorizationUrls((current) => ({
        ...current,
        [mailbox.id]: started.authorizationUrl,
      }));
      setGmailStartingId("");
    } catch (error: unknown) {
      setGmailFailure(
        error instanceof CoreApiError && error.code === "STEP_UP_REQUIRED"
          ? "安全验证时间过久（超过 12 小时）：请先退出并重新登录 CRM，再点「重新授权」；若仍旧提示，请在 Futureceed 站点退出后重新登录。"
          : error instanceof CoreApiError && error.status === 403
            ? "当前账号尚未满足连接邮箱所需的权限或额外身份验证，请联系管理员。"
            : "Gmail 授权入口生成失败，请重新登录后再试。",
      );
      setGmailStartingId("");
    }
  };

  const revokeGmail = async (mailbox: GmailMailbox) => {
    if (
      !window.confirm(
        `收回 ${mailbox.emailAddress} 的邮箱权限？后续同步会停止，已同步邮件保留。`,
      )
    )
      return;
    setGmailStartingId(mailbox.id);
    setGmailFailure("");
    setGmailNotice("");
    try {
      const result = await revokeGmailAccess(mailbox.id);
      setGmailMailboxes(await loadGmailMailboxes());
      setGmailAuthorizationUrls((current) => {
        const next = { ...current };
        delete next[mailbox.id];
        return next;
      });
      setGmailNotice(
        result.googleRevoked
          ? "邮箱权限已收回，后续同步已停止。历史邮件保留。"
          : "CRM 邮箱访问已停止。Google 端撤销尚未确认，请在 Google 账号的第三方连接中检查。历史邮件保留。",
      );
    } catch {
      setGmailFailure(
        "收回权限未完全完成，请刷新状态后重试；如登录已过期，请重新登录。",
      );
    } finally {
      setGmailStartingId("");
    }
  };

  const recoverGmail = async (mailbox: GmailMailbox) => {
    setGmailRecoveringId(mailbox.id);
    setGmailFailure("");
    setGmailNotice("");
    try {
      await syncGmailRecovery(mailbox.id);
      setGmailNotice(
        `${mailbox.emailAddress} 已提交恢复同步（最近 30 天）；完成后新邮件会进入询单中心。`,
      );
    } catch (error: unknown) {
      setGmailFailure(
        error instanceof CoreApiError && error.status === 403
          ? "当前账号没有执行 Gmail 恢复同步的权限。"
          : "恢复同步提交失败，请稍后重试或重新登录。",
      );
    } finally {
      setGmailRecoveringId("");
    }
  };

  return (
    <div className="source-page">
      <div className="content-header">
        <div>
          <div className="eyebrow">集成配置</div>
          <h2>数据源状态</h2>
        </div>
        <span className="content-count">{sources.length} 个数据源</span>
      </div>
      <div className="source-grid">
        {sources.map((source) => (
          <Card key={source.id}>
            <CardHeader>
              <div>
                <strong>{source.sourceKey}</strong>
                <small>{source.profile.key}</small>
              </div>
              <ModeTag demo={source.mode === "DEMO"} />
            </CardHeader>
            <CardContent>
              <dl className="source-facts">
                <div>
                  <dt>状态</dt>
                  <dd>
                    <Status
                      color={statusColor(source.status) as never}
                      text={statusLabel(source.status)}
                    />
                  </dd>
                </div>
                <div>
                  <dt>凭据</dt>
                  <dd>{statusLabel(source.credentialState)}</dd>
                </div>
                <div>
                  <dt>最近同步</dt>
                  <dd>
                    {source.lastSyncAt
                      ? new Date(source.lastSyncAt).toLocaleString("zh-CN")
                      : "尚未同步"}
                  </dd>
                </div>
                <div>
                  <dt>检查点</dt>
                  <dd>{source.checkpoint?.recordCount ?? 0} 条记录</dd>
                </div>
              </dl>
              <div className="source-actions">
                <Button
                  variant="secondary"
                  size="small"
                  disabled={busy}
                  onClick={() => onSchemaProbe(source.id)}
                  type="button"
                >
                  仅探测数据结构
                </Button>
                <Button variant="tertiary" size="small" type="button">
                  测试 / 演示同步
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {!gmailLoading && !gmailFailure && gmailMailboxes.length === 0 ? (
          <Card>
            <CardHeader>
              <strong>Gmail 接入</strong>
            </CardHeader>
            <CardContent>
              <p>
                当前工作空间尚未配置 Gmail
                邮箱。请联系管理员完成邮箱配置，配置后这里会显示“连接 Gmail”。
              </p>
            </CardContent>
          </Card>
        ) : null}
        {gmailMailboxes.map((mailbox) => {
          const scopes =
            mailbox.gmailAuthorization?.scopes ?? GMAIL_READONLY_SCOPES;
          const requestsModify = scopes.includes(
            "https://www.googleapis.com/auth/gmail.modify",
          );
          const requestsSend = scopes.includes(
            "https://www.googleapis.com/auth/gmail.send",
          );
          const canReauthorize =
            mailbox.authorizationStatus === "AUTHORIZED" &&
            mailbox.gmailAuthorization?.reauthorizationEnabled === true;
          return (
            <Card key={mailbox.id}>
              <CardHeader>
                <div>
                  <strong>Gmail 接入</strong>
                  <small>{mailbox.emailAddress}</small>
                </div>
                <Tag color="green" text="真实" variant="border" />
              </CardHeader>
              <CardContent>
                <dl className="source-facts">
                  <div>
                    <dt>授权状态</dt>
                    <dd>{statusLabel(mailbox.authorizationStatus)}</dd>
                  </div>
                  <div>
                    <dt>同步状态</dt>
                    <dd>{statusLabel(mailbox.syncStatus)}</dd>
                  </div>
                  <div>
                    <dt>权限范围</dt>
                    <dd>
                      {scopes
                        .map((scope) =>
                          scope.replace("https://www.googleapis.com/auth/", ""),
                        )
                        .join(" · ")}
                    </dd>
                  </div>
                </dl>
                {requestsModify ? (
                  <p>
                    重新授权用于在人工确认询单结果后，把原始邮件标记为已读；不会授予
                    CRM
                    向客户或供应商发信的能力。完成授权也不会自动开启生产执行开关。
                  </p>
                ) : null}
                {requestsSend ? (
                  <p>
                    本次授权还包含 Gmail 发信范围，CRM
                    只会在分发询单时，把该客户的原始询单邮件从
                    info@evitachem.com
                    投递给负责该分配的销售人员本人（用于销售对外报价时引用原文），以及负责该分配的查货人员本人（用于查货直接在客户会话里回复）。
                  </p>
                ) : null}
                <div className="source-actions">
                  {mailbox.authorizationStatus === "AUTHORIZED" ? (
                    <Button
                      variant="secondary"
                      size="small"
                      type="button"
                      disabled={busy || gmailStartingId === mailbox.id}
                      onClick={() => void revokeGmail(mailbox)}
                    >
                      {gmailStartingId === mailbox.id
                        ? "正在收回…"
                        : "收回邮箱权限"}
                    </Button>
                  ) : null}
                  <Button
                    variant="primary"
                    size="small"
                    disabled={
                      busy ||
                      gmailStartingId === mailbox.id ||
                      (mailbox.authorizationStatus === "AUTHORIZED" &&
                        !canReauthorize)
                    }
                    onClick={() => void startGmail(mailbox)}
                    type="button"
                  >
                    {gmailStartingId === mailbox.id
                      ? "正在准备…"
                      : canReauthorize
                        ? requestsSend
                          ? "重新授权 Gmail（标记已读 + 投递原件副本）"
                          : "重新授权 Gmail（允许标记为已读）"
                        : mailbox.authorizationStatus === "AUTHORIZED"
                          ? "已完成 Gmail 授权"
                          : gmailAuthorizationUrls[mailbox.id]
                            ? "重新生成授权入口"
                            : requestsModify
                              ? "连接 Gmail（允许标记为已读）"
                              : "连接 Gmail（只读）"}
                  </Button>
                  {gmailAuthorizationUrls[mailbox.id] ? (
                    <a
                      className="gmail-oauth-link"
                      href={gmailAuthorizationUrls[mailbox.id]}
                      target="_self"
                    >
                      {canReauthorize
                        ? "打开 Google 重新授权"
                        : "打开 Google 授权"}
                    </a>
                  ) : null}
                  {mailbox.syncStatus === "RECOVERY_REQUIRED" ? (
                    <Button
                      variant="primary"
                      size="small"
                      type="button"
                      disabled={busy || gmailRecoveringId === mailbox.id}
                      onClick={() => void recoverGmail(mailbox)}
                    >
                      {gmailRecoveringId === mailbox.id
                        ? "正在恢复…"
                        : "恢复同步（最近 30 天）"}
                    </Button>
                  ) : null}
                </div>
                <SyncedMailList key={mailbox.id} mailboxId={mailbox.id} />
              </CardContent>
            </Card>
          );
        })}
        <Card>
          <CardHeader>
            <div>
              <strong>SES 内部询单分发</strong>
              <small>AWS SES · us-east-1 · STARTTLS 587</small>
            </div>
            <Tag
              color={smtpStatus?.ready ? "green" : "yellow"}
              text={smtpStatus?.ready ? "凭据已就绪" : "等待配置"}
              variant="border"
            />
          </CardHeader>
          <CardContent>
            <dl className="source-facts">
              <div>
                <dt>凭据</dt>
                <dd>
                  {smtpStatusLoading
                    ? "正在核对…"
                    : smtpStatus?.credentialsConfigured
                      ? "用户名与密码已加密配置"
                      : "等待录入轮换后的凭据"}
                </dd>
              </div>
              <div>
                <dt>发件地址</dt>
                <dd>
                  {smtpStatus?.endpoint.fromAddressMasked ??
                    "由 Gmail 邮箱策略确定"}
                </dd>
              </div>
              <div>
                <dt>销售邮箱</dt>
                <dd>
                  {smtpStatus?.endpoint.salesRecipientMasked ??
                    "由销售团队策略确定"}
                </dd>
              </div>
              <div>
                <dt>查货邮箱</dt>
                <dd>
                  {smtpStatus?.endpoint.sourcingRecipientMasked ??
                    "由查货团队策略确定"}
                </dd>
              </div>
              <div>
                <dt>真实发送</dt>
                <dd>
                  {smtpStatus?.sendingReleased
                    ? "已由独立运行开关放行"
                    : "保持关闭，凭据录入不会自动发送"}
                </dd>
              </div>
            </dl>
            {smtpStatusFailure ? (
              <small className="smtp-secret-notice smtp-secret-notice-error">
                {smtpStatusFailure}
              </small>
            ) : null}
            {smtpVisible ? (
              <div className="smtp-secret-form" aria-label="SMTP 凭据录入">
                <label>
                  <span>SMTP Username</span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    spellCheck={false}
                    value={smtpUsername}
                    onChange={(event) => setSmtpUsername(event.target.value)}
                  />
                </label>
                <label>
                  <span>SMTP Password</span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    spellCheck={false}
                    value={smtpPassword}
                    onChange={(event) => setSmtpPassword(event.target.value)}
                  />
                </label>
                <small>
                  请录入轮换后的 AWS SES SMTP 凭据。两个字段只进入本地加密
                  Vault， 不会回显、写入仓库或 Compose。
                </small>
                <div className="source-actions">
                  <Button
                    variant="primary"
                    size="small"
                    disabled={smtpBusy || !smtpUsername.trim() || !smtpPassword}
                    onClick={() => void saveSmtp()}
                    type="button"
                  >
                    {smtpBusy ? "正在加密保存…" : "加密保存凭据"}
                  </Button>
                  <Button
                    variant="secondary"
                    size="small"
                    disabled={smtpBusy}
                    onClick={() => {
                      setSmtpVisible(false);
                      setSmtpUsername("");
                      setSmtpPassword("");
                    }}
                    type="button"
                  >
                    取消
                  </Button>
                </div>
              </div>
            ) : (
              <div className="source-actions">
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => setSmtpVisible(true)}
                  type="button"
                >
                  {smtpStatus?.credentialsConfigured
                    ? "轮换 SMTP 凭据"
                    : "录入 SMTP 凭据"}
                </Button>
              </div>
            )}
            {smtpNotice ? (
              <small className="smtp-secret-notice">{smtpNotice}</small>
            ) : null}
          </CardContent>
        </Card>
      </div>
      {gmailLoading ? (
        <div className="loading-state">
          <Loader />
        </div>
      ) : null}
      {gmailFailure ? (
        <Banner color="danger" variant="secondary">
          {gmailFailure}
        </Banner>
      ) : null}
      {gmailNotice ? <p role="status">{gmailNotice}</p> : null}
    </div>
  );
}

function DetailDrawer({
  detail,
  onClose,
  busy,
  onRisk,
}: {
  detail?: InquiryDetail;
  onClose: () => void;
  busy: boolean;
  onRisk: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
  }, []);
  useEffect(() => {
    const handle = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key !== "Tab" || !drawerRef.current) return;
      const nodes = Array.from(
        drawerRef.current.querySelectorAll<HTMLElement>(
          "button:not([disabled]), input, a[href]",
        ),
      );
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
  }, [onClose]);
  if (!detail) return null;
  return (
    <>
      <button
        className="drawer-backdrop"
        type="button"
        aria-label="关闭详情"
        onClick={onClose}
      />
      <aside
        className="detail-drawer"
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label="询单详情"
      >
        <header className="drawer-header">
          <div>
            <div className="eyebrow">询单详情</div>
            <h2>
              {detail.productDisplayName ?? detail.catalogNumber ?? "网站询单"}
            </h2>
            <small>
              {detail.sourceSystem} · {detail.sourceInquiryId}
            </small>
          </div>
          <button
            ref={closeRef}
            className="close-button"
            type="button"
            aria-label="关闭详情"
            onClick={onClose}
          >
            <IconX size={18} />
          </button>
        </header>
        <div className="drawer-scroll">
          <div className="drawer-tags">
            <ModeTag demo={detail.demo} />
            <Status
              color={statusColor(detail.status) as never}
              text={statusLabel(detail.status)}
            />
            <Status
              color={statusColor(detail.riskDisposition) as never}
              text={`风险 · ${statusLabel(detail.riskDisposition)}`}
            />
          </div>
          <section className="detail-section">
            <h3>记录信息</h3>
            <dl className="facts-grid">
              <div>
                <dt>联系人</dt>
                <dd>{detail.contactName ?? detail.normalizedEmail}</dd>
              </div>
              <div>
                <dt>公司</dt>
                <dd>{detail.companyName ?? "未确认组织"}</dd>
              </div>
              <div>
                <dt>来源</dt>
                <dd>{detail.sourceSystem}</dd>
              </div>
              <div>
                <dt>接收时间</dt>
                <dd>
                  {new Date(detail.sourceSubmittedAt).toLocaleString("zh-CN")}
                </dd>
              </div>
              <div>
                <dt>优先级</dt>
                <dd>
                  <Tag
                    color={statusColor(detail.priority) as never}
                    text={statusLabel(detail.priority)}
                    variant="border"
                  />
                </dd>
              </div>
              <div>
                <dt>负责人</dt>
                <dd>{detail.assignedUserId ?? "未分配"}</dd>
              </div>
            </dl>
          </section>
          <section className="detail-section">
            <h3>产品 / 请求</h3>
            <p className="request-copy">
              {detail.catalogNumber ?? "目录编号待确认"} ·{" "}
              {detail.casRaw ?? "CAS 待确认"} ·{" "}
              {detail.quantityRaw ?? "数量待确认"}
            </p>
            <p>{detail.safeCommentExcerpt ?? "没有可显示的安全摘要。"}</p>
          </section>
          <section className="detail-section">
            <h3>
              客户 / 组织候选 <span>{detail.matchCandidates.length}</span>
            </h3>
            {detail.matchCandidates.length ? (
              detail.matchCandidates.map((candidate) => (
                <div className="candidate-row" key={candidate.id}>
                  <span>{candidate.method}</span>
                  <small>
                    {confidenceLabel(candidate.confidence)} ·{" "}
                    {statusLabel(candidate.status)}
                  </small>
                </div>
              ))
            ) : (
              <p className="muted">尚未生成候选。</p>
            )}
          </section>
          <section className="detail-section">
            <h3>风险审核</h3>
            <div className="inline-action">
              <Status
                color={statusColor(detail.riskDisposition) as never}
                text={statusLabel(detail.riskDisposition)}
              />
              <Button
                variant="secondary"
                size="small"
                disabled={busy}
                onClick={onRisk}
                type="button"
              >
                标记已审核
              </Button>
            </div>
          </section>
          <section className="detail-section">
            <h3>案件 / 报价</h3>
            {detail.cases.length ? (
              detail.cases.map((item) => (
                <div className="linked-record" key={item.id}>
                  <strong>{item.caseNumber}</strong>
                  <small>
                    {statusLabel(item.status)} · {item.customerQuotes.length}{" "}
                    份报价
                  </small>
                </div>
              ))
            ) : (
              <EmptyState
                title="尚未创建案件"
                description="案件创建仍由 CRM 核心服务权限和业务合同决定。"
              />
            )}
          </section>
          <section className="detail-section">
            <h3>来源版本与审计</h3>
            <p className="muted">
              当前版本 {detail.currentVersion} · 共 {detail.versions.length}{" "}
              个不可变版本。审计历史为只读。
            </p>
          </section>
        </div>
      </aside>
    </>
  );
}

type OperationRecord = CustomerSummary | ContactSummary | CaseSummary;
type OperationDetail = CustomerDetail | ContactDetail | CaseDetail;

function operationLabel(kind: OperationsKind) {
  return kind === "customers"
    ? "客户"
    : kind === "contacts"
      ? "联系人"
      : "案件";
}

function OperationsWorkspace({
  kind,
  onOpen,
  refreshKey,
}: {
  kind: OperationsKind;
  onOpen: (kind: OperationsKind, id: string) => void;
  refreshKey: number;
}) {
  const [records, setRecords] = useState<OperationRecord[]>([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState("");
  const [sort, setSort] = useState<"recent" | "oldest" | "name">("recent");
  const load = useCallback(async () => {
    setLoading(true);
    setFailure("");
    try {
      const params = new URLSearchParams({ page: "1", limit: "50", sort });
      if (query.trim()) params.set("search", query.trim());
      if (status !== "ALL") params.set("status", status);
      const result =
        kind === "customers"
          ? await loadCustomers(params.toString())
          : kind === "contacts"
            ? await loadContacts(params.toString())
            : await loadCases(params.toString());
      setRecords(result.items as OperationRecord[]);
    } catch (error) {
      setFailure(describeCoreError(error));
    } finally {
      setLoading(false);
    }
  }, [kind, query, sort, status, refreshKey]);
  useEffect(() => {
    void load();
  }, [load]);
  const statusOptions =
    kind === "customers"
      ? ["ACTIVE", "PROSPECT", "INACTIVE"]
      : kind === "contacts"
        ? ["ACTIVE", "INACTIVE"]
        : ["NEW", "IN_PROGRESS", "WAITING_CUSTOMER", "CLOSED"];
  return (
    <section className="operations-layout">
      <aside className="view-rail">
        <div className="rail-heading">
          <span>视图</span>
          <Button
            variant="tertiary"
            size="small"
            title="已保存视图尚未持久化"
            type="button"
            disabled
          >
            +
          </Button>
        </div>
        <button type="button" className="rail-view is-active">
          <span>全部 {operationLabel(kind)}</span>
          <strong>{records.length}</strong>
        </button>
        <div className="rail-divider" />
        <span className="rail-note">已保存视图 · 本地会话草稿</span>
        <span className="rail-note">核心服务写入合同待完成</span>
      </aside>
      <div className="inbox-content">
        <div className="content-header">
          <div>
            <div className="eyebrow">核心业务运营</div>
            <h2>{operationLabel(kind)}</h2>
          </div>
          <div className="content-count">{records.length} 条记录</div>
        </div>
        <div className="toolbar">
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder={`搜索 ${operationLabel(kind)}`}
            aria-label={`搜索${operationLabel(kind)}`}
          />
          <CrmSelect
            label="状态"
            value={status}
            onChange={setStatus}
            options={[
              { value: "ALL", label: "全部状态" },
              ...statusOptions.map((value) => ({
                value,
                label: statusLabel(value),
              })),
            ]}
          />
          <CrmSelect
            label="排序"
            value={sort}
            onChange={(value) => setSort(value as typeof sort)}
            options={[
              { value: "recent", label: "最近活动" },
              { value: "oldest", label: "最早创建" },
              { value: "name", label: "名称" },
            ]}
          />
        </div>
        {loading ? (
          <div className="loading-state">
            <Loader />
          </div>
        ) : failure ? (
          <Banner color="danger" variant="secondary">
            {failure}
          </Banner>
        ) : records.length ? (
          <OperationTable kind={kind} records={records} onOpen={onOpen} />
        ) : (
          <EmptyState
            title={`没有 ${operationLabel(kind)} 记录`}
            description="调整筛选条件或确认当前工作空间权限。"
          />
        )}
      </div>
    </section>
  );
}

function OperationTable({
  kind,
  records,
  onOpen,
}: {
  kind: OperationsKind;
  records: OperationRecord[];
  onOpen: (kind: OperationsKind, id: string) => void;
}) {
  const columns =
    kind === "customers"
      ? ["客户 / 域名", "负责人", "关联记录", "最近活动"]
      : kind === "contacts"
        ? ["联系人 / 组织", "来源", "关联记录", "最近活动"]
        : ["案件 / 客户", "优先级", "关注状态", "最近活动"];
  return (
    <div className="record-table operation-table" role="table">
      <div className="table-head" role="row">
        <span>{columns[0]}</span>
        <span>{columns[1]}</span>
        <span>{columns[2]}</span>
        <span>{columns[3]}</span>
        <span>状态</span>
      </div>
      {records.map((record) => (
        <button
          type="button"
          role="row"
          className="table-row"
          key={record.id}
          onClick={() => onOpen(kind, record.id)}
        >
          {kind === "customers" ? (
            <CustomerRow record={record as CustomerSummary} />
          ) : kind === "contacts" ? (
            <ContactRow record={record as ContactSummary} />
          ) : (
            <CaseRow record={record as CaseSummary} />
          )}
        </button>
      ))}
    </div>
  );
}

function CustomerRow({ record }: { record: CustomerSummary }) {
  return (
    <>
      <span className="primary-cell">
        <strong>{record.displayName}</strong>
        <small>
          {record.normalizedDomain ?? record.website ?? "域名待确认"}
        </small>
        {record.suppressed ? (
          <span className="customer-suppression-tag">
            <Tag color="red" text="不再合作" variant="border" />
          </span>
        ) : null}
      </span>
      <span className="primary-cell">
        <strong>{record.ownerUserId ? "已分配" : "未分配"}</strong>
        <small>{statusLabel(record.relationshipStatus)}</small>
      </span>
      <span className="primary-cell">
        <strong>{record.contactCount} 位联系人</strong>
        <small>
          {record.inquiryCount} 条询单 · {record.caseCount} 个案件
        </small>
      </span>
      <span className="time-cell">
        <strong>
          {new Date(record.lastActivityAt).toLocaleDateString("zh-CN")}
        </strong>
        <small>{record.site?.name ?? "未绑定站点"}</small>
      </span>
      <span>
        <Status
          color={statusColor(record.status) as never}
          text={statusLabel(record.status)}
        />
      </span>
    </>
  );
}
function ContactRow({ record }: { record: ContactSummary }) {
  return (
    <>
      <span className="primary-cell">
        <strong>{record.displayName}</strong>
        <small>
          {record.organization?.displayName ?? "未确认组织"} ·{" "}
          {record.normalizedEmail}
        </small>
      </span>
      <span className="primary-cell">
        <strong>{record.source}</strong>
        <small>{record.title ?? "职位待确认"}</small>
      </span>
      <span className="primary-cell">
        <strong>{record.inquiryCount} 条询单</strong>
        <small>
          {record.caseCount} 个案件{" "}
          {record.duplicateWarning ? "· 重复记录提醒" : ""}
        </small>
      </span>
      <span className="time-cell">
        <strong>
          {new Date(record.lastActivityAt).toLocaleDateString("zh-CN")}
        </strong>
        <small>{record.site?.name ?? "未绑定站点"}</small>
      </span>
      <span>
        <Status
          color={statusColor(record.status) as never}
          text={statusLabel(record.status)}
        />
      </span>
    </>
  );
}
function CaseRow({ record }: { record: CaseSummary }) {
  return (
    <>
      <span className="primary-cell">
        <strong>{record.caseNumber}</strong>
        <small>
          {record.subject} ·{" "}
          {record.customerOrganization?.displayName ?? "客户待确认"}
        </small>
      </span>
      <span>
        <Tag
          color={statusColor(record.priority) as never}
          text={statusLabel(record.priority)}
          variant="border"
        />
      </span>
      <span className="primary-cell">
        <strong>{statusLabel(record.attention)}</strong>
        <small>
          {record.ageHours} 小时 · {record._count.tasks} 个任务
        </small>
      </span>
      <span className="time-cell">
        <strong>
          {new Date(record.updatedAt).toLocaleDateString("zh-CN")}
        </strong>
        <small>{record.site?.name ?? "未绑定站点"}</small>
      </span>
      <span>
        <Status
          color={statusColor(record.status) as never}
          text={statusLabel(record.status)}
        />
      </span>
    </>
  );
}

function OperationsDrawer({
  kind,
  detail,
  onClose,
  onOpen,
}: {
  kind: OperationsKind;
  detail?: OperationDetail;
  onClose: () => void;
  onOpen: (kind: OperationsKind, id: string) => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
  }, [detail]);
  useEffect(() => {
    const handle = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
  }, [onClose]);
  if (!detail) return null;
  const title =
    kind === "customers"
      ? (detail as CustomerDetail).displayName
      : kind === "contacts"
        ? (detail as ContactDetail).displayName
        : (detail as CaseDetail).caseNumber;
  return (
    <>
      <button
        className="drawer-backdrop"
        type="button"
        aria-label="关闭详情"
        onClick={onClose}
      />
      <aside
        className="detail-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`${operationLabel(kind)}详情`}
      >
        <header className="drawer-header">
          <div>
            <div className="eyebrow">{operationLabel(kind)}详情</div>
            <h2>{title}</h2>
            <small>CRM 核心服务 ID · {detail.id}</small>
          </div>
          <button
            ref={closeRef}
            className="close-button"
            type="button"
            aria-label="关闭详情"
            onClick={onClose}
          >
            <IconX size={18} />
          </button>
        </header>
        <div className="drawer-scroll">
          <div className="drawer-tags">
            <Tag
              color="purple"
              text={detail.workspaceId ? "工作空间范围" : "核心服务"}
              variant="border"
            />
            <Status
              color={statusColor(detail.status) as never}
              text={statusLabel(detail.status)}
            />
          </div>
          {kind === "customers" ? (
            <CustomerDetailBody
              detail={detail as CustomerDetail}
              onOpen={onOpen}
            />
          ) : kind === "contacts" ? (
            <ContactDetailBody
              detail={detail as ContactDetail}
              onOpen={onOpen}
            />
          ) : (
            <CaseDetailBody detail={detail as CaseDetail} onOpen={onOpen} />
          )}
        </div>
      </aside>
    </>
  );
}

function CustomerDetailBody({
  detail,
  onOpen,
}: {
  detail: CustomerDetail;
  onOpen: (kind: OperationsKind, id: string) => void;
}) {
  return (
    <>
      {detail.suppressed ? (
        <p className="customer-suppression-note">
          <Tag color="red" text="不再合作" variant="border" />
          <span>
            该客户域名或联系人邮箱在“不再合作名单”中：其后续询单会在进入分诊前自动判为无效询单。
            可在询单工作台的“不再合作名单”中解除。
          </span>
        </p>
      ) : null}
      <DetailFacts
        facts={[
          ["域名", detail.normalizedDomain ?? "—"],
          ["负责人", detail.ownerUserId ?? "未分配"],
          ["站点", detail.site?.name ?? "—"],
          [
            "关联记录",
            `${detail.contactCount} 位联系人 · ${detail.caseCount} 个案件`,
          ],
        ]}
      />
      <RelationSection title="联系人" count={detail.contacts.length}>
        {detail.contacts.map((contact) => (
          <button
            type="button"
            className="linked-record linked-button"
            key={contact.id}
            onClick={() => onOpen("contacts", contact.id)}
          >
            <strong>{contact.displayName}</strong>
            <small>
              {contact.normalizedEmail} · {statusLabel(contact.status)}
            </small>
          </button>
        ))}
      </RelationSection>
      <RelationSection title="案件" count={detail.inquiryCases.length}>
        {detail.inquiryCases.map((item) => (
          <button
            type="button"
            className="linked-record linked-button"
            key={item.id}
            onClick={() => onOpen("cases", item.id)}
          >
            <strong>{item.caseNumber}</strong>
            <small>
              {statusLabel(item.status)} · {item._count.tasks} 个任务 ·{" "}
              {item._count.internalNotes} 条内部备注
            </small>
          </button>
        ))}
      </RelationSection>
      <AuditSection count={detail.audit.length} />
    </>
  );
}
function ContactDetailBody({
  detail,
  onOpen,
}: {
  detail: ContactDetail;
  onOpen: (kind: OperationsKind, id: string) => void;
}) {
  return (
    <>
      <DetailFacts
        facts={[
          ["邮箱", detail.normalizedEmail],
          ["职位", detail.title ?? "—"],
          ["组织", detail.customerOrganization?.displayName ?? "未确认组织"],
          ["负责人", detail.ownerUserId ?? "未分配"],
        ]}
      />
      {detail.customerOrganization ? (
        <button
          type="button"
          className="linked-record linked-button"
          onClick={() => onOpen("customers", detail.customerOrganization!.id)}
        >
          <strong>打开客户 · {detail.customerOrganization.displayName}</strong>
          <small>
            {detail.customerOrganization.normalizedDomain ?? "域名待确认"}
          </small>
        </button>
      ) : null}
      <RelationSection title="案件" count={detail.inquiryCases.length}>
        {detail.inquiryCases.map((item) => (
          <button
            type="button"
            className="linked-record linked-button"
            key={item.id}
            onClick={() => onOpen("cases", item.id)}
          >
            <strong>{item.caseNumber}</strong>
            <small>
              {statusLabel(item.status)} · {item._count.tasks} 个任务
            </small>
          </button>
        ))}
      </RelationSection>
      <RelationSection title="候选记录审核" count={detail.candidates.length}>
        {detail.candidates.map((candidate) => (
          <div className="candidate-row" key={candidate.id}>
            <span>{candidate.method}</span>
            <small>
              {confidenceLabel(candidate.confidence)} ·{" "}
              {statusLabel(candidate.status)}
            </small>
          </div>
        ))}
      </RelationSection>
      <AuditSection count={detail.audit.length} />
    </>
  );
}
function CaseDetailBody({
  detail,
  onOpen,
}: {
  detail: CaseDetail;
  onOpen: (kind: OperationsKind, id: string) => void;
}) {
  return (
    <>
      <DetailFacts
        facts={[
          ["主题", detail.subject],
          ["优先级", statusLabel(detail.priority)],
          ["负责人", detail.ownerUserId ?? "未分配"],
          ["SLA", statusLabel(detail.attention)],
          ["站点", detail.site?.name ?? "—"],
        ]}
      />
      {detail.customerOrganization ? (
        <button
          type="button"
          className="linked-record linked-button"
          onClick={() => onOpen("customers", detail.customerOrganization!.id)}
        >
          <strong>客户 · {detail.customerOrganization.displayName}</strong>
          <small>打开客户详情</small>
        </button>
      ) : null}
      {detail.primaryContact ? (
        <button
          type="button"
          className="linked-record linked-button"
          onClick={() => onOpen("contacts", detail.primaryContact!.id)}
        >
          <strong>联系人 · {detail.primaryContact.displayName}</strong>
          <small>{detail.primaryContact.normalizedEmail}</small>
        </button>
      ) : null}
      <RelationSection title="来源询单" count={detail.inquiries.length}>
        {detail.inquiries.map((item) => (
          <div className="linked-record" key={item.id}>
            <strong>{item.sourceInquiryId}</strong>
            <small>
              {item.productDisplayName ?? item.catalogNumber ?? "产品待确认"} ·{" "}
              {statusLabel(item.priority)}
            </small>
          </div>
        ))}
      </RelationSection>
      <RelationSection
        title="任务 / 内部备注"
        count={detail.tasks.length + detail.internalNotes.length}
      >
        {detail.tasks.map((task) => (
          <div className="linked-record" key={task.id}>
            <strong>{task.title}</strong>
            <small>
              {statusLabel(task.status)} ·{" "}
              {task.dueAt
                ? new Date(task.dueAt).toLocaleDateString("zh-CN")
                : "无截止日期"}
            </small>
          </div>
        ))}
        {detail.internalNotes.slice(0, 3).map((note) => (
          <div className="linked-record" key={note.id}>
            <strong>内部备注</strong>
            <small>{new Date(note.createdAt).toLocaleString("zh-CN")}</small>
          </div>
        ))}
      </RelationSection>
      <AuditSection count={detail.audit.length} />
    </>
  );
}
function DetailFacts({ facts }: { facts: Array<[string, string]> }) {
  return (
    <section className="detail-section">
      <dl className="facts-grid">
        {facts.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
function RelationSection({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section className="detail-section">
      <h3>
        {title} <span>{count}</span>
      </h3>
      {count ? children : <p className="muted">暂无关联记录。</p>}
    </section>
  );
}
function AuditSection({ count }: { count: number }) {
  return (
    <section className="detail-section">
      <h3>审计摘要</h3>
      <p className="muted">{count} 条安全只读事件 · 已隐藏载荷和安全上下文。</p>
    </section>
  );
}

function CommandMenu({
  open,
  onClose,
  onView,
  onOpenOperation,
  onOpenInquiry,
  workspaces,
  currentWorkspaceId,
  onSwitchWorkspace,
}: {
  open: boolean;
  onClose: () => void;
  onView: (view: ViewKey) => void;
  onOpenOperation: (kind: OperationsKind, id: string) => void;
  onOpenInquiry: (id: string) => void;
  workspaces: AuthenticatedWorkspace[];
  currentWorkspaceId?: string;
  onSwitchWorkspace: (workspaceId: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [groups, setGroups] = useState<OperationsSearchGroup[]>([]);
  useEffect(() => {
    if (!open || query.trim().length < 2) {
      setGroups([]);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void searchOperations(query.trim(), controller.signal)
        .then((result) => setGroups(result.groups))
        .catch(() => setGroups([]));
    }, 180);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [open, query]);
  if (!open) return null;
  return (
    <div
      className="command-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="命令菜单"
      onClick={onClose}
    >
      <div
        className="command-menu"
        onClick={(event) => event.stopPropagation()}
      >
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder="搜索 CRM 记录或命令"
          autoFocus
          aria-label="搜索 CRM 记录"
        />
        {query.trim().length >= 2 ? (
          <div className="command-results">
            {groups.length ? (
              groups.map((group) => (
                <div key={group.type}>
                  <div className="command-group-label">
                    {objectTypeLabel(group.type)}
                  </div>
                  {group.items.map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      onClick={() => {
                        if (group.type === "INQUIRY") onOpenInquiry(item.id);
                        else
                          onOpenOperation(
                            group.type === "CUSTOMER"
                              ? "customers"
                              : group.type === "CONTACT"
                                ? "contacts"
                                : "cases",
                            item.id,
                          );
                        onClose();
                      }}
                    >
                      <span>
                        {String(
                          item.displayName ??
                            item.caseNumber ??
                            item.sourceInquiryId ??
                            item.subject ??
                            item.id,
                        )}
                      </span>
                      <small>
                        {String(item.matchedOn ?? "")} ·{" "}
                        {String(
                          item.site &&
                            typeof item.site === "object" &&
                            "name" in item.site
                            ? item.site.name
                            : "工作空间",
                        )}
                      </small>
                    </button>
                  ))}
                </div>
              ))
            ) : (
              <p className="muted command-empty">没有有权查看的记录</p>
            )}
          </div>
        ) : (
          <>
            {workspaces.length > 1 ? (
              <div className="command-group-label">工作空间</div>
            ) : null}
            {workspaces.map((workspace) => (
              <button
                type="button"
                key={workspace.id}
                aria-current={
                  workspace.id === currentWorkspaceId ? "true" : undefined
                }
                onClick={() => {
                  onSwitchWorkspace(workspace.id);
                  onClose();
                }}
              >
                <span>切换到 {workspace.name}</span>
                <small>
                  {businessTypeLabel(workspace.workspaceType)} ·{" "}
                  {statusLabel(workspace.status)}
                  {workspace.id === currentWorkspaceId ? " · 当前" : ""}
                </small>
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                onView("inbox");
                onClose();
              }}
            >
              打开询单 <kbd>G I</kbd>
            </button>
            <button
              type="button"
              onClick={() => {
                onView("customers");
                onClose();
              }}
            >
              打开客户
            </button>
            <button
              type="button"
              onClick={() => {
                onView("cases");
                onClose();
              }}
            >
              打开案件
            </button>
            <button
              type="button"
              onClick={() => {
                onView("overview");
                onClose();
              }}
            >
              打开总览
            </button>
          </>
        )}
        <button type="button" onClick={onClose}>
          关闭 <kbd>Esc</kbd>
        </button>
      </div>
    </div>
  );
}

export default function App() {
  const initialView = (getParam("view") as ViewKey) || "overview";
  const [view, setView] = useState<ViewKey>(initialView);
  const [dashboard, setDashboard] = useState<InquiryDashboard>();
  const [overview, setOverview] = useState<OverviewDashboard>();
  const [overviewPeriod, setOverviewPeriod] = useState<OverviewPeriod>("TODAY");
  const [operationsContext, setOperationsContext] =
    useState<OperationsContext>();
  const [workspaces, setWorkspaces] = useState<AuthenticatedWorkspace[]>([]);
  const [inquiries, setInquiries] = useState<InquirySummary[]>([]);
  const [sources, setSources] = useState<InquirySource[]>([]);
  const [selected, setSelected] = useState<InquiryDetail>();
  const [loading, setLoading] = useState(true);
  const [bootstrapLoading, setBootstrapLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState("");
  const [authenticationRequired, setAuthenticationRequired] = useState(false);
  const [authenticatedIdentity, setAuthenticatedIdentity] =
    useState<AuthenticatedIdentity>();
  const [commandOpen, setCommandOpen] = useState(false);
  const [operationDetail, setOperationDetail] = useState<OperationDetail>();
  const [operationKind, setOperationKind] = useState<OperationsKind>();
  const [operationLoading, setOperationLoading] = useState(false);
  const [operationsRefreshKey, setOperationsRefreshKey] = useState(0);
  const workforceView =
    WORKFORCE_TABS.includes(view) && view !== "team-members"
      ? (view as WorkforceView)
      : undefined;
  const commercialTab = commercialTabFor(view);
  const showShellPageHeader =
    view === "overview" ||
    (WORKFORCE_TABS.includes(view) && view !== "team-members");
  const requestedMode = getParam("mode");
  const requestedCaseId = getParam("case");
  const inquiryCenterExperience = resolveInquiryCenterExperience({
    requestedMode,
    inquiryRead: operationsContext?.permissions.inquiryRead,
    inquiryReview: operationsContext?.permissions.inquiryReview,
    triageOnly: operationsContext?.permissions.triageOnly,
    triageDispositionManage:
      operationsContext?.permissions.triageDispositionManage,
    emailIntelligenceRead: operationsContext?.permissions.emailIntelligenceRead,
  });
  const finalTriageWorkbench =
    inquiryCenterExperience === "FINAL_TRIAGE_WORKBENCH";
  const inquiryView = ["inbox", "risk", "sources"].includes(view);
  const refreshOverview = useCallback(async (period: OverviewPeriod) => {
    setLoading(true);
    setFailure("");
    try {
      setOverview(await loadOverview(period));
    } catch (error) {
      if (error instanceof CoreApiError && error.status === 401)
        setAuthenticationRequired(true);
      setFailure(describeCoreError(error));
    } finally {
      setLoading(false);
    }
  }, []);
  const refresh = useCallback(async () => {
    setLoading(true);
    setFailure("");
    try {
      const data = await loadInquiryWorkspace();
      setDashboard(data.dashboard);
      setInquiries(data.inquiries);
      setSources(data.sources);
      const inquiryId = getParam("inquiry");
      if (inquiryId) setSelected(await loadInquiry(inquiryId));
    } catch (error) {
      if (error instanceof CoreApiError && error.status === 401)
        setAuthenticationRequired(true);
      setFailure(describeCoreError(error));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    let active = true;
    const bootstrap = async () => {
      setBootstrapLoading(true);
      setFailure("");
      try {
        const [workspaceResult, identity] = await Promise.all([
          loadAuthenticatedWorkspaces(),
          loadAuthenticatedIdentity(),
        ]);
        if (!active) return;
        if (
          !workspaceResult.selectedWorkspaceId &&
          workspaceResult.workspaces[0]
        ) {
          await selectAuthenticatedWorkspace(workspaceResult.workspaces[0].id);
        }
        const context = await loadOperationsContext();
        if (!active) return;
        const nextView = resolveInitialView(
          getParam("view"),
          context.permissions,
          context.code,
          window.location.hostname,
        );
        setOperationsContext(context);
        setWorkspaces(workspaceResult.workspaces);
        setAuthenticatedIdentity(identity);
        setView(nextView);
        if (nextView !== getParam("view")) {
          const url = new URL(window.location.href);
          url.searchParams.set("view", nextView);
          window.history.replaceState(null, "", url);
        }
        if (["role-management", "user-management"].includes(nextView)) {
          setLoading(false);
          return;
        }
        if (
          ["inquiry-center", "approvals-exceptions"].includes(nextView) &&
          resolveInquiryCenterExperience({
            requestedMode,
            inquiryRead: context.permissions.inquiryRead,
            inquiryReview: context.permissions.inquiryReview,
            triageOnly: context.permissions.triageOnly,
            triageDispositionManage:
              context.permissions.triageDispositionManage,
            emailIntelligenceRead: context.permissions.emailIntelligenceRead,
          }) === "FINAL_TRIAGE_WORKBENCH"
        ) {
          setLoading(false);
          return;
        }
        if (nextView === "overview") await refreshOverview(overviewPeriod);
        else if (["inbox", "risk", "sources"].includes(nextView))
          await refresh();
        else setLoading(false);
      } catch (error) {
        if (!active) return;
        if (error instanceof CoreApiError && error.status === 401) {
          setAuthenticationRequired(true);
        }
        setFailure(describeCoreError(error));
      } finally {
        if (active) setBootstrapLoading(false);
      }
    };
    void bootstrap();
    return () => {
      active = false;
    };
  }, [refresh, refreshOverview]);
  useEffect(() => {
    const handle = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandOpen(true);
      }
      if (event.key === "/" && document.activeElement?.tagName !== "INPUT") {
        event.preventDefault();
        document
          .querySelector<HTMLInputElement>('[aria-label="搜索询单"]')
          ?.focus();
      }
    };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, []);
  const selectView = (next: ViewKey) => {
    const canonicalView = canonicalizeInquiryCenterView(
      next,
      operationsContext?.permissions,
    ) as ViewKey;
    setView(canonicalView);
    setSelected(undefined);
    setOperationDetail(undefined);
    setOperationKind(undefined);
    window.history.replaceState(null, "", `/?view=${canonicalView}`);
    if (canonicalView === "overview") void refreshOverview(overviewPeriod);
  };
  const selectInquiry = async (item: InquirySummary) => {
    setBusy(true);
    try {
      setSelected(await loadInquiry(item.id));
      window.history.replaceState(
        null,
        "",
        `/?view=${view}&inquiry=${item.id}`,
      );
    } catch (error) {
      setFailure(describeCoreError(error));
    } finally {
      setBusy(false);
    }
  };
  const actRisk = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await setRisk(selected, "RESOLVED");
      await refresh();
      setSelected(await loadInquiry(selected.id));
    } catch (error) {
      setFailure(describeCoreError(error));
    } finally {
      setBusy(false);
    }
  };
  const bulk = async () => {
    setBusy(true);
    try {
      await Promise.all(
        inquiries.slice(0, 20).map((item) => setPriority(item, "HIGH")),
      );
      await refresh();
    } catch (error) {
      setFailure(describeCoreError(error));
    } finally {
      setBusy(false);
    }
  };
  const probeSourceSchema = async (sourceId: string) => {
    setBusy(true);
    setFailure("");
    try {
      await probeInquirySourceSchema(sourceId);
      await refresh();
    } catch (error) {
      setFailure(describeCoreError(error));
    } finally {
      setBusy(false);
    }
  };
  const closeDrawer = () => {
    setSelected(undefined);
    window.history.replaceState(null, "", `/?view=${view}`);
  };
  const loadOperation = async (
    kind: OperationsKind,
    id: string,
    pushHistory: boolean,
  ) => {
    setOperationLoading(true);
    setFailure("");
    setOperationKind(kind);
    setView(kind);
    try {
      const detail =
        kind === "customers"
          ? await loadCustomer(id)
          : kind === "contacts"
            ? await loadContact(id)
            : await loadCase(id);
      setOperationDetail(detail as OperationDetail);
      if (pushHistory) {
        window.history.pushState(
          null,
          "",
          `/?view=${kind}&record=${encodeURIComponent(id)}`,
        );
      }
    } catch (error) {
      setFailure(describeCoreError(error));
    } finally {
      setOperationLoading(false);
    }
  };
  const openOperation = (kind: OperationsKind, id: string) =>
    loadOperation(kind, id, true);
  const closeOperationDrawer = () => {
    setOperationDetail(undefined);
    setOperationKind(undefined);
    window.history.replaceState(null, "", `/?view=${view}`);
  };
  const openInquiryFromSearch = (id: string) => {
    const nextView = canonicalizeInquiryCenterView(
      "inbox",
      operationsContext?.permissions,
    ) as ViewKey;
    setView(nextView);
    setSelected(undefined);
    window.history.pushState(
      null,
      "",
      `/?view=${nextView}&inquiry=${encodeURIComponent(id)}`,
    );
    void loadInquiry(id)
      .then(setSelected)
      .catch((error) => setFailure(describeCoreError(error)));
  };
  const switchWorkspace = async (workspaceId: string) => {
    setLoading(true);
    setFailure("");
    setSelected(undefined);
    setOperationDetail(undefined);
    setOperationKind(undefined);
    try {
      await selectAuthenticatedWorkspace(workspaceId);
      resetCaseRealtimeConnection();
      const context = await loadOperationsContext();
      const nextView =
        view === "overview" || view === "personal-settings"
          ? view
          : (view === "configuration-mailboxes" &&
                context.permissions.mailboxRead) ||
              (view === "configuration-basic" &&
                canView(view, context.permissions))
            ? view
            : ["cases", "cases-won", "cases-returned", "cases-failed"].includes(
                  view,
                ) && canViewCaseQueue(view, context.permissions)
              ? view
              : view === "role-management" && context.permissions.roleRead
                ? "role-management"
                : view === "user-management" && context.permissions.userRead
                  ? "user-management"
                  : view === "team-members" &&
                      context.permissions.workforceTeamRead
                    ? "team-members"
                    : defaultViewForPermissions(context.permissions);
      if (nextView === "overview") {
        setOverview(await loadOverview(overviewPeriod));
      } else if (
        ![
          "configuration-basic",
          "configuration-mailboxes",
          "personal-settings",
          "role-management",
          "user-management",
          "team-members",
          "cases",
          "cases-won",
          "cases-returned",
          "cases-failed",
        ].includes(nextView)
      ) {
        const workspace = await loadInquiryWorkspace();
        setDashboard(workspace.dashboard);
        setInquiries(workspace.inquiries);
        setSources(workspace.sources);
      }
      setOperationsContext(context);
      setView(nextView);
      setOperationsRefreshKey((value) => value + 1);
      window.history.replaceState(null, "", `/?view=${nextView}`);
    } catch (error) {
      setFailure(describeCoreError(error));
    } finally {
      setLoading(false);
    }
  };
  const logout = async () => {
    try {
      await logoutAuthenticatedSession();
    } finally {
      window.location.assign("/");
    }
  };
  useEffect(() => {
    if (!operationsContext) return;
    const restoreFromUrl = () => {
      const nextView = resolveInitialView(
        getParam("view"),
        operationsContext.permissions,
        operationsContext.code,
        window.location.hostname,
      );
      const recordId = getParam("record");
      const inquiryId = getParam("inquiry");
      setView(nextView);
      if (["customers", "contacts", "cases"].includes(nextView) && recordId) {
        setSelected(undefined);
        void loadOperation(nextView as OperationsKind, recordId, false);
      } else {
        setOperationDetail(undefined);
        setOperationKind(undefined);
        if (nextView === "inbox" && inquiryId) {
          void loadInquiry(inquiryId)
            .then(setSelected)
            .catch((error) => setFailure(describeCoreError(error)));
        } else {
          setSelected(undefined);
        }
      }
    };
    restoreFromUrl();
    window.addEventListener("popstate", restoreFromUrl);
    return () => window.removeEventListener("popstate", restoreFromUrl);
  }, [operationsContext]);
  if (authenticationRequired) return <AuthenticationRequired />;
  if (bootstrapLoading) return <AppBootstrapSkeleton />;
  return (
    <AppShell
      view={view}
      onView={selectView}
      demo={dashboard?.mode !== "REAL"}
      context={operationsContext}
      userName={authenticatedIdentity?.user.displayName}
      administratorType={authenticatedIdentity?.user.administratorType}
      workspaces={workspaces}
      currentWorkspaceId={operationsContext?.id}
      onSwitchWorkspace={(workspaceId) => void switchWorkspace(workspaceId)}
      onLogout={() => void logout()}
      onCommand={() => setCommandOpen(true)}
    >
      {!operationsContext?.permissions.triageOnly && showShellPageHeader ? (
        <PageHeader
          dashboard={dashboard}
          view={view}
          context={operationsContext}
          onRefresh={() => {
            if (view === "overview") void refreshOverview(overviewPeriod);
            else void refresh();
            setOperationsRefreshKey((value) => value + 1);
          }}
        />
      ) : null}
      {dashboard?.coverageNotice &&
      ["inbox", "risk", "sources"].includes(view) ? (
        <div className="coverage-line">
          <span className="online-dot" />
          {dashboard.coverageNotice}
        </div>
      ) : null}
      {loading && ["overview", "inbox", "risk", "sources"].includes(view) ? (
        <div className="loading-state">
          <Loader />
        </div>
      ) : null}
      {(inquiryView || view === "overview") && failure && !loading ? (
        <Banner color="danger" variant="secondary">
          {failure}
        </Banner>
      ) : null}
      {!loading && !failure && view === "overview" ? (
        <Dashboard
          overview={overview}
          period={overviewPeriod}
          busy={loading}
          onPeriod={(period) => {
            setOverviewPeriod(period);
            void refreshOverview(period);
          }}
          onView={selectView}
        />
      ) : null}
      {!loading && !failure && view === "inbox" ? (
        <Inbox
          items={inquiries}
          selectedId={selected?.id}
          onSelect={(item) => void selectInquiry(item)}
          onBulk={() => void bulk()}
          busy={busy}
        />
      ) : null}
      {!loading && !failure && view === "risk" ? (
        <Inbox
          items={inquiries.filter((item) => item.riskDisposition !== "CLEAR")}
          selectedId={selected?.id}
          onSelect={(item) => void selectInquiry(item)}
          onBulk={() => void bulk()}
          busy={busy}
        />
      ) : null}
      {!loading && !failure && view === "sources" ? (
        <SourceHealth
          sources={sources}
          busy={busy}
          onSchemaProbe={(sourceId) => void probeSourceSchema(sourceId)}
        />
      ) : null}
      {["customers", "contacts"].includes(view) ? (
        <OperationsWorkspace
          key={view}
          kind={view as OperationsKind}
          onOpen={(kind, id) => void openOperation(kind, id)}
          refreshKey={operationsRefreshKey}
        />
      ) : null}
      {["cases", "cases-won", "cases-returned", "cases-failed"].includes(
        view,
      ) ? (
        <CaseWorkspace
          key={operationsContext?.id}
          outcome={
            view === "cases-won"
              ? "WON"
              : view === "cases-returned"
                ? "RETURNED"
                : view === "cases-failed"
                  ? "FAILED"
                  : "PENDING"
          }
          initialId={requestedCaseId || undefined}
        />
      ) : null}
      {view === "configuration-basic" && !loading && operationsContext ? (
        <BasicConfiguration
          key={operationsContext.id}
          workspaceName={operationsContext.name}
          canConfigureMail={Boolean(
            operationsContext.permissions.userManage &&
            operationsContext.permissions.roleManage,
          )}
          canConfigureMetrics={Boolean(
            operationsContext.permissions.caseMetricDefinitionsManage ||
            (operationsContext.permissions.userManage &&
              operationsContext.permissions.roleManage),
          )}
        />
      ) : null}
      {view === "personal-settings" && !loading && operationsContext ? (
        <PersonalSettings
          key={operationsContext.id}
          workspaceName={operationsContext.name}
          permissions={operationsContext.permissions}
        />
      ) : null}
      {view === "configuration-workspaces" ? (
        <WorkspaceManagement
          currentWorkspaceId={operationsContext?.id}
          onDirectoryChanged={async (currentWorkspaceDisabled) => {
            const result = await loadAuthenticatedWorkspaces();
            setWorkspaces(result.workspaces);
            if (currentWorkspaceDisabled) {
              const next = result.workspaces[0];
              if (next) await selectAuthenticatedWorkspace(next.id);
              window.location.replace("/?view=configuration-workspaces");
              return;
            }
            setOperationsContext(await loadOperationsContext());
          }}
        />
      ) : null}
      {view === "configuration-mailboxes" ? (
        <MailboxConfiguration key={operationsContext?.id} />
      ) : null}
      {workforceView ? (
        <WorkforceDevApp
          key={`${operationsContext?.id ?? "workspace"}-${operationsRefreshKey}-${workforceView}`}
          view={workforceView}
          embedded
          onViewChange={(next) => selectView(next)}
        />
      ) : null}
      {commercialTab ? (
        <CommercialDevelopmentApp
          key={`${operationsContext?.id ?? "workspace"}-${operationsRefreshKey}-${commercialTab}`}
          tab={commercialTab}
          embedded
          onTabChange={(next) => selectView(commercialViewFor(next))}
        />
      ) : null}
      {view === "shadow-flow" && operationsContext?.id ? (
        <ShadowFlowWorkspace
          key={`${operationsContext.id}-${operationsRefreshKey}`}
          workspaceId={operationsContext.id}
        />
      ) : null}
      {view === "people-work-allocation" &&
      operationsContext?.permissions.triageScopeManage ? (
        <PeopleWorkAllocation />
      ) : null}
      {view === "role-management" && operationsContext?.permissions.roleRead ? (
        <RoleManagement
          key={operationsContext.id}
          onSaved={async () => {
            const context = await loadOperationsContext();
            setOperationsContext(context);
            if (!context.permissions.roleRead) setView("overview");
          }}
        />
      ) : null}
      {view === "user-management" && operationsContext?.permissions.userRead ? (
        <UserManagement
          key={operationsContext.id}
          currentWorkspaceId={operationsContext.id}
        />
      ) : null}
      {view === "team-members" &&
      operationsContext?.permissions.workforceTeamRead ? (
        <TeamManagement key={operationsContext.id} />
      ) : null}
      {view === "email-intelligence" &&
      operationsContext?.permissions.emailIntelligenceRead ? (
        <EmailIntelligenceReview
          canReview={
            operationsContext?.permissions.emailIntelligenceReview ?? false
          }
          canRun={operationsContext?.permissions.emailIntelligenceRun ?? false}
        />
      ) : null}
      {view === "inquiry-center" && finalTriageWorkbench && !requestedCaseId ? (
        <>
          {operationsContext?.permissions.mailboxRead &&
          operationsContext.permissions.mailboxSync ? (
            <InquiryCenterGmailSyncAction />
          ) : null}
          <TriageWorkspace
            workspaceCode={operationsContext?.code}
            pilotMode={
              requestedMode === "real-shadow" || finalTriageWorkbench
                ? "REAL_SHADOW"
                : "CORE"
            }
            enableAgentAutoRun={
              operationsContext?.permissions.aiRunCreate ?? false
            }
          />
        </>
      ) : null}
      {view === "approvals-exceptions" && finalTriageWorkbench ? (
        <TriageWorkspace
          workspaceCode={operationsContext?.code}
          exceptions
          pilotMode={
            requestedMode === "real-shadow" || finalTriageWorkbench
              ? "REAL_SHADOW"
              : "CORE"
          }
          enableAgentAutoRun={
            operationsContext?.permissions.aiRunCreate ?? false
          }
        />
      ) : null}
      {view === "inquiry-center" &&
      (!finalTriageWorkbench || Boolean(requestedCaseId)) ? (
        <CoreBusinessFlowWorkspace
          initialCaseId={requestedCaseId || undefined}
        />
      ) : null}
      {view === "approvals-exceptions" && !finalTriageWorkbench ? (
        <CoreBusinessFlowWorkspace mode="exceptions" />
      ) : null}
      {view === "developer-test-mail" &&
      canUseDevTesterTestMail(
        operationsContext?.code,
        window.location.hostname,
      ) ? (
        <DevTesterTestMailWorkspace workspaceCode={operationsContext?.code} />
      ) : null}
      {view === "developer-test-mail" &&
      !canUseDevTesterTestMail(
        operationsContext?.code,
        window.location.hostname,
      ) ? (
        <PlannedFeatureWorkspace
          title="发送测试邮件"
          description="当前测试邮件工具仅在 DevTester 工作空间启用；菜单位置保持统一。"
        />
      ) : null}
      {operationLoading ? (
        <div className="loading-state">
          <Loader />
        </div>
      ) : null}
      <DetailDrawer
        detail={selected}
        onClose={closeDrawer}
        busy={busy}
        onRisk={() => void actRisk()}
      />
      <OperationsDrawer
        kind={operationKind ?? "customers"}
        detail={operationDetail}
        onClose={closeOperationDrawer}
        onOpen={(kind, id) => void openOperation(kind, id)}
      />
      <CommandMenu
        open={commandOpen}
        onClose={() => setCommandOpen(false)}
        onView={selectView}
        onOpenOperation={(kind, id) => void openOperation(kind, id)}
        onOpenInquiry={openInquiryFromSearch}
        workspaces={workspaces}
        currentWorkspaceId={operationsContext?.id}
        onSwitchWorkspace={(workspaceId) => void switchWorkspace(workspaceId)}
      />
    </AppShell>
  );
}

function commercialTabFor(view: ViewKey): CommercialTab | undefined {
  if (view === "suppliers") return "suppliers";
  if (view === "supplier-rfqs") return "rfqs";
  if (view === "supplier-quotes") return "supplier-quotes";
  if (view === "customer-quotes") return "quotes";
  if (view === "approvals") return "approvals";
  return undefined;
}

function resolveInitialView(
  requested: string,
  permissions: OperationsContext["permissions"] | undefined,
  workspaceCode?: string,
  hostname = window.location.hostname,
): ViewKey {
  requested = canonicalizeInquiryCenterView(requested, permissions);
  if (!Object.prototype.hasOwnProperty.call(VIEW_LABELS, requested)) {
    return defaultViewForPermissions(permissions);
  }
  const requestedView = requested as ViewKey;
  if (requestedView === "developer-test-mail") {
    return canUseDevTesterTestMail(workspaceCode, hostname) ||
      (permissions?.userManage && permissions?.roleManage)
      ? requestedView
      : defaultViewForPermissions(permissions);
  }
  if (!canView(requestedView, permissions)) {
    return defaultViewForPermissions(permissions);
  }
  if (
    [
      "role-management",
      "user-management",
      "configuration-mailboxes",
      "configuration-basic",
    ].includes(requestedView)
  )
    return requestedView;
  if (permissions?.triageOnly)
    return [
      "overview",
      "personal-settings",
      "inquiry-center",
      "approvals-exceptions",
      "email-intelligence",
    ].includes(requested)
      ? requestedView
      : "inquiry-center";
  return requestedView;
}

function commercialViewFor(tab: CommercialTab): CommercialViewKey {
  if (tab === "rfqs") return "supplier-rfqs";
  if (tab === "supplier-quotes") return "supplier-quotes";
  if (tab === "quotes") return "customer-quotes";
  return tab;
}

function canView(
  view: ViewKey,
  permissions: OperationsContext["permissions"] | undefined,
): boolean {
  if (view === "role-management") return permissions?.roleRead ?? false;
  if (view === "user-management") return permissions?.userRead ?? false;
  if (view === "configuration-mailboxes")
    return permissions?.mailboxRead ?? false;
  if (view === "configuration-basic")
    return Boolean(
      permissions?.caseMetricDefinitionsManage ||
      (permissions?.userManage && permissions?.roleManage),
    );
  if (view === "configuration-workspaces")
    return Boolean(
      permissions?.workspaceManage || permissions?.workspaceCreate,
    );
  if (view === "overview" || view === "personal-settings") return true;
  if (permissions?.triageOnly)
    return (
      view === "inquiry-center" ||
      view === "approvals-exceptions" ||
      (view === "email-intelligence" &&
        (permissions.emailIntelligenceRead ?? false))
    );
  if (view === "people-work-allocation")
    return permissions?.triageScopeManage ?? false;
  if (!permissions) return false;
  if (view === "inquiry-center") return permissions.inquiryRead;
  if (view === "approvals-exceptions")
    return permissions.approvalRead || permissions.inquiryReview;
  if (["inbox", "sources"].includes(view)) return permissions.inquiryRead;
  if (view === "risk") return permissions.inquiryReview;
  if (["customers", "contacts"].includes(view)) return permissions.customerRead;
  if (["cases", "cases-won", "cases-returned", "cases-failed"].includes(view))
    return canViewCaseQueue(view, permissions);
  if (view === "developer-test-mail")
    return Boolean(permissions.userManage && permissions.roleManage);
  if (view === "my-work") return permissions.workforceRead;
  if (view === "dispatch") return permissions.workforceDispatch;
  if (["team-queue", "team-members"].includes(view))
    return permissions.workforceTeamRead;
  if (view === "sla-escalation") return permissions.workforceSlaRead;
  if (view === "suppliers") return permissions.supplierRead;
  if (view === "supplier-rfqs") return permissions.supplierRfqRead;
  if (view === "supplier-quotes") return permissions.supplierQuoteRead;
  if (view === "customer-quotes") return permissions.customerQuoteRead;
  if (view === "approvals") return permissions.approvalRead;
  if (view === "shadow-flow") return permissions.inquiryRead;
  if (view === "email-intelligence")
    return permissions.emailIntelligenceRead ?? false;
  return false;
}

function defaultViewForPermissions(
  permissions: OperationsContext["permissions"] | undefined,
): ViewKey {
  if (!permissions) return "overview";
  if (permissions.triageOnly) return "inquiry-center";
  if (
    permissions.overviewInquiryDistributionRead ||
    permissions.overviewCaseManagementRead
  )
    return "overview";
  if (permissions.inquiryRead) return "inquiry-center";
  if (permissions.casePendingRead ?? permissions.caseRead) return "cases";
  if (permissions.caseWonRead) return "cases-won";
  if (permissions.caseReturnedRead) return "cases-returned";
  if (permissions.caseFailedRead) return "cases-failed";
  if (permissions.customerRead) return "customers";
  if (permissions.roleRead) return "role-management";
  if (permissions.userRead) return "user-management";
  return "overview";
}

function canViewCaseQueue(
  view: string,
  permissions: OperationsContext["permissions"] | undefined,
): boolean {
  if (!permissions) return false;
  if (view === "cases-won")
    return permissions.caseWonRead ?? permissions.caseRead;
  if (view === "cases-returned")
    return permissions.caseReturnedRead ?? permissions.caseRead;
  if (view === "cases-failed")
    return permissions.caseFailedRead ?? permissions.caseRead;
  return permissions.casePendingRead ?? permissions.caseRead;
}
